import React, {useEffect, useMemo, useState, useRef} from "react";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {supabase} from "./utils/supabaseClient";
import { valuesEqualCountryAware, normalizeConditionalRequirements } from "./utils/conditionalFieldValues";
import { getAppOrigin } from "./utils/url";
import { getEffectiveCertPolicy, requiresEmailVerification, getNextRegistrationStage } from "./utils/enrollmentPolicy";
import {
    Alert,
    Box,
    Button,
    Center,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Select,
    Spinner,
    Stack,
    Text,
    AlertIcon,
    useToast,
    Badge,
    SimpleGrid,
    VStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Flex,
    Icon,
    IconButton,
    Checkbox,
    Textarea,
    Card,
    CardBody,
    CardHeader,
    HStack,
} from "@chakra-ui/react";
import {HiChevronDown, HiChevronUp} from 'react-icons/hi';
import { FaHandPointer } from 'react-icons/fa';
import PhoneInput, { isValidPhoneNumber as isValidPhone, parsePhoneNumber as parsePhone } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import {parsePhoneNumber, isValidPhoneNumber} from 'libphonenumber-js';
import moment from 'moment';
import NRIC from 'singapore-nric';
import AdditionalFields from "./AdditionalFields";
import { motion } from "framer-motion";
import { useRouteConfig } from "./providers/RouteConfigProvider";
import HelpPopup from './components/HelpPopup';
import countryList from 'react-select-country-list';
import RegistrationSearchableSelect from './components/RegistrationSearchableSelect';
import { fetchPublicRegistration } from './utils/registrationPortalApi';

const isDevelopment = process.env.NODE_ENV === 'development';

/** Splits "Primary//Secondary" labels into two lines. */
function BilingualLabel({ label, isRequired, requiredColor = "red.500" }) {
    const delim = '//';
    const slashIdx = label ? label.indexOf(delim) : -1;
    const primary = slashIdx === -1 ? label : label.slice(0, slashIdx).trim();
    const secondary = slashIdx === -1 ? null : label.slice(slashIdx + delim.length).trim();
    return (
        <>
            {primary}
            {isRequired && <Text as="span" color={requiredColor} ml={1}>*</Text>}
            {secondary && (
                <Text as="div" fontSize="xs" fontWeight="normal" fontStyle="italic" color="gray.500" mt={0.5} lineHeight="tight">
                    {secondary}
                </Text>
            )}
        </>
    );
}

const devLog = (...args) => {
    if (isDevelopment) {
        console.log(...args);
    }
};

function shouldSkipSessionEdgeInvoke(kind, registrationId) {
    try {
        if (typeof sessionStorage === 'undefined' || !registrationId) return false;
        return sessionStorage.getItem(`lms_ef_${kind}_${registrationId}`) === '1';
    } catch (_) {
        return false;
    }
}

function rememberSessionEdgeInvoke(kind, registrationId) {
    try {
        if (typeof sessionStorage === 'undefined' || !registrationId) return;
        sessionStorage.setItem(`lms_ef_${kind}_${registrationId}`, '1');
    } catch (_) {}
}

function queueRegistrationReceivedEmail(registrationId) {
    if (shouldSkipSessionEdgeInvoke('registration_received', registrationId)) {
        console.warn('[FullCertRegistration] Skipping duplicate send-registration-received-email invoke (same tab session)');
        return;
    }
    supabase.functions
        .invoke('send-registration-received-email', { body: { registrationId } })
        .then(({ error }) => {
            if (error) {
                console.error('send-registration-received-email error:', error);
            } else {
                rememberSessionEdgeInvoke('registration_received', registrationId);
                devLog('Registration received email queued');
            }
        })
        .catch((emailErr) => {
            console.error('send-registration-received-email threw:', emailErr);
        });
}

// Fallback option values for nationality/ID-type when field_options is empty (values stored as-is; display uses field_behaviors.option_labels when present)
const DEFAULT_NATIONALITY_OPTION_VALUES = [
    "Singapore Citizen / PR",
    "Work Pass/ E-Pass / LTVP (FIN)",
    "Non-Singapore Resident (Passport No)"
];

// Floating Progress Bar Component - shows compulsory field completion
const FloatingProgressBar = ({ 
    filledCount, 
    totalCount, 
    emailVerified, 
    emailVerificationSent,
    participantEmail,
    onResendVerification,
    isSendingVerification,
    showEmailVerification
}) => {
    const progressPercent = totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 0;
    const isComplete = filledCount === totalCount;
    
    return (
        <Box
            position="sticky"
            top={0}
            left={0}
            right={0}
            zIndex={100}
            bg="white"
            borderBottom="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
            transition="all 0.3s ease"
        >
            {/* Progress Bar */}
            <Box px={{base: 4, md: 6}} py={{base: 2, md: 3}}>
                <Flex align="center" justify="space-between" mb={1}>
                    <HStack spacing={2}>
                        <Text fontSize={{base: "xs", md: "sm"}} fontWeight="600" color="gray.700">
                            {isComplete ? "✓ All fields complete" : "Form Progress"}
                        </Text>
                    </HStack>
                    <Badge 
                        colorScheme={isComplete ? "green" : progressPercent > 50 ? "blue" : "orange"}
                        fontSize={{base: "10px", md: "xs"}}
                        px={2}
                    >
                        {filledCount}/{totalCount}
                    </Badge>
                </Flex>
                <Box 
                    w="100%" 
                    h={{base: "4px", md: "6px"}} 
                    bg="gray.100" 
                    borderRadius="full" 
                    overflow="hidden"
                >
                    <Box
                        h="100%"
                        w={`${progressPercent}%`}
                        bgGradient={isComplete 
                            ? "linear(to-r, green.400, green.500)" 
                            : "linear(to-r, brand.400, brand.600)"
                        }
                        borderRadius="full"
                        transition="width 0.4s ease-out"
                    />
                </Box>
            </Box>
            
            {/* Email Verification Notice - Only shows when needed */}
            {showEmailVerification && participantEmail && !emailVerified && (
                <Box 
                    px={{base: 4, md: 6}} 
                    py={{base: 2, md: 2.5}}
                    bg="orange.50"
                    borderTop="1px solid"
                    borderColor="orange.100"
                >
                    <Flex 
                        align="center" 
                        justify="space-between"
                        gap={2}
                        flexWrap={{base: "wrap", md: "nowrap"}}
                    >
                        <HStack spacing={2} flex="1" minW={0}>
                            <Text fontSize="md">📧</Text>
                            <Text 
                                fontSize={{base: "xs", md: "sm"}} 
                                color="orange.800"
                                fontWeight="500"
                                isTruncated
                            >
                                Verify: <Text as="span" fontWeight="600">{participantEmail}</Text>
                            </Text>
                        </HStack>
                        <Button
                            size="xs"
                            colorScheme="orange"
                            variant="solid"
                            onClick={onResendVerification}
                            isLoading={isSendingVerification}
                            loadingText="..."
                            flexShrink={0}
                        >
                            Resend
                        </Button>
                    </Flex>
                </Box>
            )}
            
            {/* Email Verified Success - Brief confirmation */}
            {showEmailVerification && emailVerified && (
                <Box 
                    px={{base: 4, md: 6}} 
                    py={2}
                    bg="green.50"
                    borderTop="1px solid"
                    borderColor="green.100"
                >
                    <HStack spacing={2}>
                        <Text fontSize="sm">✓</Text>
                        <Text fontSize="xs" color="green.700" fontWeight="600">
                            Email verified
                        </Text>
                    </HStack>
                </Box>
            )}
        </Box>
    );
};

const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

const validateNRIC = (nric, nationality) => {
    if (!nric) return false;
    
    // Only validate NRIC format for Singapore Citizens and PRs
    // For other nationalities, allow free text (passport/FIN numbers)
    // Support both old and new option values
    const isSingaporeCitizenOrPR = 
        nationality === "Singapore Citizen" || 
        nationality === "Singapore Permanent Resident" ||
        nationality === "Singapore Citizen / PR";
    
    if (!isSingaporeCitizenOrPR) {
        // For non-Singapore citizens/PRs, just check that something is entered
        return nric.trim().length > 0;
    }
    
    // For Singapore citizens/PRs, validate NRIC format
    const nricInstance = new NRIC(nric);
    return nricInstance.isValid && nricInstance.isCorrectFormat;
};

// Enhanced calendar component for module dates with duration
const ModuleCalendar = ({course_dates, moduleCode}) => {
    if (!course_dates || course_dates.length === 0) return null;

    const sessionsWithDuration = course_dates.map(session => {
        const startTime = moment(session.start_time, "HHmm");
        const endTime = moment(session.end_time, "HHmm");
        const duration = endTime.diff(startTime, 'hours', true);

        return {
            ...session,
            duration: duration,
            durationText: duration >= 1 ? `${Math.floor(duration)}h ${Math.round((duration % 1) * 60)}m` : `${Math.round(duration * 60)}m`
        };
    });

    const datesByMonth = sessionsWithDuration.reduce((acc, session) => {
        const date = new Date(session.date);
        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
        const monthName = date.toLocaleDateString('en-US', {month: 'short', year: 'numeric'});

        if (!acc[monthKey]) {
            acc[monthKey] = {
                monthName,
                dates: []
            };
        }

        acc[monthKey].dates.push({
            day: date.getDate(),
            startTime: session.start_time,
            endTime: session.end_time,
            duration: session.duration,
            durationText: session.durationText,
            dayOfWeek: date.toLocaleDateString('en-US', {weekday: 'short'})
        });

        return acc;
    }, {});

    const totalDuration = sessionsWithDuration.reduce((sum, session) => sum + session.duration, 0);
    const totalHours = Math.floor(totalDuration);
    const totalMinutes = Math.round((totalDuration % 1) * 60);

    return (
        <Box mt={2}>
            <Text fontSize="xs" fontWeight="700" color="gray.600" mb={2.5} textTransform="uppercase" letterSpacing="0.5px">
                Schedule
            </Text>
            <VStack spacing={3} align="stretch">
                {Object.values(datesByMonth).map((month, index) => (
                    <Box key={index}>
                        <Text fontSize="xs" fontWeight="700" color="gray.700" mb={2} textTransform="uppercase" letterSpacing="0.5px">
                            {month.monthName}
                        </Text>

                        <Box overflowX="auto" borderRadius="md" border="1px solid" borderColor="gray.200">
                            <Table variant="simple" size="sm" colorScheme="gray">
                                <Thead bg="gray.50">
                                    <Tr>
                                        <Th fontSize="xs" fontWeight="700" color="gray.700" p={2.5} borderBottom="1px solid" borderColor="gray.200">Date</Th>
                                        <Th fontSize="xs" fontWeight="700" color="gray.700" p={2.5} borderBottom="1px solid" borderColor="gray.200">Time</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {month.dates.map((date, dateIndex) => {
                                        const startTimeFormatted = moment(date.startTime, "HHmm").format("h:mm A");
                                        const endTimeFormatted = moment(date.endTime, "HHmm").format("h:mm A");
                                        const timeRange = `${startTimeFormatted} - ${endTimeFormatted}`;
                                        
                                        return (
                                            <Tr key={dateIndex} _hover={{ bg: "blue.50" }} transition="background 0.2s">
                                                <Td fontSize="xs" p={2.5} borderBottom="1px solid" borderColor="gray.100">
                                                    <VStack spacing={0.5} align="start">
                                                        <Text fontWeight="700" fontSize="sm" color="gray.800">{date.day}</Text>
                                                        <Text fontSize="10px" color="gray.600" textTransform="capitalize" fontWeight="500">
                                                            {date.dayOfWeek}
                                                        </Text>
                                                    </VStack>
                                                </Td>
                                                <Td fontSize="xs" p={2.5} borderBottom="1px solid" borderColor="gray.100">
                                                    <Text fontWeight="600" fontSize="xs" color="gray.800">
                                                        {timeRange}
                                                    </Text>
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                    </Box>
                ))}
            </VStack>
        </Box>
    );
};

// Module card component for displaying certification modules with flip animation
function ModuleCard({course_name, start_date, end_date, course_dates, course_type_code, module_tag}) {
    const [isFlipped, setIsFlipped] = useState(false);
    const frontCardRef = useRef(null);
    const backCardRef = useRef(null);
    const containerRef = useRef(null);
    
    // Format dates for display
    const startDateFormatted = start_date ? moment(start_date).format('MMM DD, YYYY') : 'TBD';
    const endDateFormatted = end_date ? moment(end_date).format('MMM DD, YYYY') : 'TBD';
    const isUpcoming = start_date ? moment(start_date).isAfter(moment()) : false;
    const daysUntilStart = start_date ? moment(start_date).diff(moment(), 'days') : null;
    
    // Get first session date for sorting/display
    const firstSessionDate = course_dates && course_dates.length > 0 
        ? course_dates[0]?.date 
        : start_date;

    // Prepare schedule data for the back of the card
    const sessionsWithDuration = (course_dates || []).map(session => {
        const startTime = moment(session.start_time, "HHmm");
        const endTime = moment(session.end_time, "HHmm");
        const duration = endTime.diff(startTime, 'hours', true);
        return {
            ...session,
            duration: duration,
            durationText: duration >= 1 ? `${Math.floor(duration)}h ${Math.round((duration % 1) * 60)}m` : `${Math.round(duration * 60)}m`
        };
    });

    const datesByMonth = sessionsWithDuration.reduce((acc, session) => {
        const date = new Date(session.date);
        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
        const monthName = date.toLocaleDateString('en-US', {month: 'short', year: 'numeric'});

        if (!acc[monthKey]) {
            acc[monthKey] = {
                monthName,
                dates: []
            };
        }

        acc[monthKey].dates.push({
            day: date.getDate(),
            startTime: session.start_time,
            endTime: session.end_time,
            duration: session.duration,
            durationText: session.durationText,
            dayOfWeek: date.toLocaleDateString('en-US', {weekday: 'short'})
        });

        return acc;
    }, {});

    // Update container height based on visible card
    useEffect(() => {
        const updateHeight = () => {
            if (containerRef.current) {
                const visibleCard = isFlipped ? backCardRef.current : frontCardRef.current;
                if (visibleCard) {
                    containerRef.current.style.height = `${visibleCard.offsetHeight}px`;
                }
            }
        };
        
        updateHeight();
        
        // Also update on window resize
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [isFlipped]);

    return (
        <Box
            ref={containerRef}
            cursor="pointer"
            onClick={() => setIsFlipped(!isFlipped)}
            sx={{
                perspective: '1000px'
            }}
            transition="height 0.6s"
        >
            <Box
                position="relative"
                width="100%"
                transition="transform 0.6s"
                sx={{
                    transformStyle: 'preserve-3d',
                    transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                }}
            >
                {/* Front of card */}
                <Box
                    ref={frontCardRef}
                    position="absolute"
                    width="100%"
                    borderWidth='1px'
                    borderRadius='xl'
                    boxShadow='sm'
                    borderColor="gray.200"
                    bg="white"
                    px={{base: 3, md: 4}}
                    py={{base: 3, md: 4}}
                    wordBreak="break-word"
                    display="flex"
                    flexDirection="column"
                    transition="box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out"
                    _hover={{
                        boxShadow: 'lg',
                        borderColor: 'brand.400'
                    }}
                    overflow="hidden"
                    sx={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden'
                    }}
                >
                    {/* Accent bar at top */}
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        height="4px"
                        bgGradient="linear(to-r, brand.400, brand.600)"
                    />
                    
                    <VStack align="stretch" spacing={3} flex={1}>
                        {/* Header with badge and code */}
                        <HStack justify="space-between" align="flex-start">
                            <Badge 
                                colorScheme="blue" 
                                fontSize="xs" 
                                px={2.5} 
                                py={1} 
                                borderRadius="md"
                                fontWeight="600"
                                textTransform="uppercase"
                                letterSpacing="0.5px"
                            >
                                {course_type_code}
                            </Badge>
                            {isUpcoming && daysUntilStart !== null && daysUntilStart >= 0 && (
                                <Badge 
                                    colorScheme={daysUntilStart <= 7 ? "orange" : "green"} 
                                    fontSize="xs" 
                                    px={2} 
                                    py={1}
                                    borderRadius="md"
                                >
                                    {daysUntilStart === 0 ? 'Starts Today' : 
                                     daysUntilStart === 1 ? 'Starts Tomorrow' :
                                     `In ${daysUntilStart} days`}
                                </Badge>
                            )}
                        </HStack>

                        {/* Module name */}
                        <Text 
                            fontSize={{base: "sm", md: "md"}} 
                            fontWeight="700" 
                            color="gray.800"
                            lineHeight="1.4"
                            mb={1}
                        >
                            {course_name}
                        </Text>

                        {/* Date range */}
                        <Box 
                            p={{base: 2, md: 2.5}} 
                            bg="gray.50" 
                            borderRadius="md"
                            borderLeft="3px solid"
                            borderColor="brand.400"
                        >
                            <VStack align="stretch" spacing={1}>
                                <HStack spacing={2}>
                                    <Text fontSize={{base: "10px", md: "xs"}} fontWeight="600" color="gray.600" textTransform="uppercase" letterSpacing="0.5px">
                                        Start:
                                    </Text>
                                    <Text fontSize={{base: "xs", md: "sm"}} fontWeight="600" color="gray.800">
                                        {startDateFormatted}
                                    </Text>
                                </HStack>
                                <HStack spacing={2}>
                                    <Text fontSize={{base: "10px", md: "xs"}} fontWeight="600" color="gray.600" textTransform="uppercase" letterSpacing="0.5px">
                                        End:
                                    </Text>
                                    <Text fontSize={{base: "xs", md: "sm"}} fontWeight="600" color="gray.800">
                                        {endDateFormatted}
                                    </Text>
                                </HStack>
                            </VStack>
                        </Box>

                        {/* Click hint */}
                        <Box mt="auto" pt={2}>
                            <Text fontSize={{base: "10px", md: "xs"}} color="gray.500" textAlign="center" fontStyle="italic">
                                Click to view schedule
                            </Text>
                        </Box>
                    </VStack>
                </Box>

                {/* Back of card - Schedule */}
                <Box
                    ref={backCardRef}
                    position="absolute"
                    width="100%"
                    top="0"
                    left="0"
                    borderWidth='1px'
                    borderRadius='xl'
                    boxShadow='sm'
                    borderColor="gray.200"
                    bg="white"
                    px={{base: 3, md: 4}}
                    py={{base: 3, md: 4}}
                    wordBreak="break-word"
                    display="flex"
                    flexDirection="column"
                    overflow="auto"
                    sx={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)'
                    }}
                >
                    {/* Accent bar at top */}
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        height="4px"
                        bgGradient="linear(to-r, brand.400, brand.600)"
                    />
                    
                    <VStack align="stretch" spacing={3} flex={1}>
                        <HStack justify="space-between" align="center" flexWrap="wrap" spacing={2}>
                            <Text fontSize={{base: "xs", md: "sm"}} fontWeight="700" color="gray.600" textTransform="uppercase" letterSpacing="0.5px">
                                Schedule
                            </Text>
                            <Badge 
                                colorScheme="blue" 
                                fontSize={{base: "10px", md: "xs"}} 
                                px={{base: 2, md: 2.5}} 
                                py={1} 
                                borderRadius="md"
                                fontWeight="600"
                                textTransform="uppercase"
                            >
                                {course_type_code}
                            </Badge>
                        </HStack>

                        {Object.keys(datesByMonth).length > 0 ? (
                            <VStack spacing={3} align="stretch">
                                {Object.values(datesByMonth).map((month, index) => (
                                    <Box key={index}>
                                        <Text fontSize={{base: "10px", md: "xs"}} fontWeight="700" color="gray.700" mb={2} textTransform="uppercase" letterSpacing="0.5px">
                                            {month.monthName}
                                        </Text>

                                        <Box overflowX="auto" borderRadius="md" border="1px solid" borderColor="gray.200" w="100%">
                                            <Table variant="simple" size="sm" colorScheme="gray" minW="200px">
                                                <Thead bg="gray.50">
                                                    <Tr>
                                                        <Th fontSize={{base: "10px", md: "xs"}} fontWeight="700" color="gray.700" p={{base: 2, md: 2.5}} borderBottom="1px solid" borderColor="gray.200">Date</Th>
                                                        <Th fontSize={{base: "10px", md: "xs"}} fontWeight="700" color="gray.700" p={{base: 2, md: 2.5}} borderBottom="1px solid" borderColor="gray.200">Time</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {month.dates.map((date, dateIndex) => {
                                                        const startTimeFormatted = moment(date.startTime, "HHmm").format("h:mm A");
                                                        const endTimeFormatted = moment(date.endTime, "HHmm").format("h:mm A");
                                                        const timeRange = `${startTimeFormatted} - ${endTimeFormatted}`;
                                                        
                                                        return (
                                                            <Tr key={dateIndex} _hover={{ bg: "blue.50" }} transition="background 0.2s">
                                                                <Td fontSize={{base: "10px", md: "xs"}} p={{base: 2, md: 2.5}} borderBottom="1px solid" borderColor="gray.100">
                                                                    <VStack spacing={0.5} align="start">
                                                                        <Text fontWeight="700" fontSize={{base: "xs", md: "sm"}} color="gray.800">{date.day}</Text>
                                                                        <Text fontSize={{base: "9px", md: "10px"}} color="gray.600" textTransform="capitalize" fontWeight="500">
                                                                            {date.dayOfWeek}
                                                                        </Text>
                                                                    </VStack>
                                                                </Td>
                                                                <Td fontSize={{base: "10px", md: "xs"}} p={{base: 2, md: 2.5}} borderBottom="1px solid" borderColor="gray.100">
                                                                    <Text fontWeight="600" fontSize={{base: "10px", md: "xs"}} color="gray.800">
                                                                        {timeRange}
                                                                    </Text>
                                                                </Td>
                                                            </Tr>
                                                        );
                                                    })}
                                                </Tbody>
                                            </Table>
                                        </Box>
                                    </Box>
                                ))}
                            </VStack>
                            ) : (
                                <Text fontSize={{base: "xs", md: "sm"}} color="gray.500" textAlign="center" py={4}>
                                    No schedule available
                                </Text>
                            )}

                        {/* Click hint */}
                        <Box mt="auto" pt={2}>
                            <Text fontSize={{base: "10px", md: "xs"}} color="gray.500" textAlign="center" fontStyle="italic">
                                Click to return
                            </Text>
                        </Box>
                    </VStack>
                </Box>
            </Box>
        </Box>
    );
}

const FullCertRegistration = () => {
    const [courseRuns, setCourseRuns] = useState([]);
    const [groupedCourseRuns, setGroupedCourseRuns] = useState({});
    const [loadingCourseRuns, setLoadingCourseRuns] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fullCertificationCost, setFullCertificationCost] = useState(null);
    const [selectedCourseRunIds, setSelectedCourseRunIds] = useState([]);
    const [registrationId, setRegistrationId] = useState(null);
    const [loadingRegistration, setLoadingRegistration] = useState(false);
    const [additionalFields, setAdditionalFields] = useState([]);
    const [basicFieldsOrder, setBasicFieldsOrder] = useState([]);
    const [compulsoryCustomFields, setCompulsoryCustomFields] = useState([]);
    const [selectedCertName, setSelectedCertName] = useState('');
    const [showModules, setShowModules] = useState(false);
    const [templateFieldIds, setTemplateFieldIds] = useState(null);
    
    // Organization GST settings
    const [orgGstSettings, setOrgGstSettings] = useState({
        gst_enabled: false,
        gst_rate: 0
    });
    
    // File upload state for compulsory fields
    const [selectedFiles, setSelectedFiles] = useState({});
    const [uploadingFiles, setUploadingFiles] = useState(new Set());

    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const { fullCertRegistrationPath } = useRouteConfig();
    const fullCertificationId = searchParams.get('full_certification');
    console.log(fullCertificationId, "fullCertificationId")

    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    const additionalFieldsRef = useRef(null);
    const hasShownLoadToast = useRef(false);

    const [formData, setFormData] = useState({
        participant_name: '',
        participant_email: '',
        nationality: '',
        birth_age: '',
        nric: '',
        mobile_no: '',
        mobile_no_raw: '',
        dob: '',
        displayDob: '',
        draft_cert_run_code: '',
        full_certification_id: '',
    });
    
    // Progress calculation moved after state definitions - see below

    // -----------------------
    // Local draft (localStorage)
    // -----------------------
    const LOCAL_DRAFT_PREFIX = 'full_cert_registration_draft:';
    const localDraftKey = id ? `${LOCAL_DRAFT_PREFIX}${id}` : null;

    const sanitizeFormDataForStorage = (data) => {
        // localStorage has size limits; do not store large blobs (e.g., signatures) or non-serializable values.
        const cleaned = {};
        Object.entries(data || {}).forEach(([k, v]) => {
            if (k.toLowerCase().includes('signature')) return; // skip signature base64
            if (k === 'registration_stage') return; // server is the source of truth
            if (v instanceof File) return;
            if (Array.isArray(v) && v.some((x) => x instanceof File)) return;
            if (typeof v === 'string' && v.length > 200000) return; // safety guard
            cleaned[k] = v;
        });
        return cleaned;
    };

    const readLocalDraft = () => {
        if (!localDraftKey) return null;
        try {
            const raw = window.localStorage.getItem(localDraftKey);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            return parsed || null;
        } catch {
            return null;
        }
    };

    const writeLocalDraft = (data) => {
        if (!localDraftKey) return;
        try {
            window.localStorage.setItem(
                localDraftKey,
                JSON.stringify({ ts: Date.now(), formData: sanitizeFormDataForStorage(data) })
            );
        } catch (e) {
            // Quota exceeded or blocked storage; fail silently.
            devLog('localStorage write failed', e);
        }
    };

    const clearLocalDraft = () => {
        if (!localDraftKey) return;
        try {
            window.localStorage.removeItem(localDraftKey);
        } catch {
            // ignore
        }
    };

    const [validationErrors, setValidationErrors] = useState({
        email: false,
        mobile: false,
        invalidDomain: false,
        dob: false,
        nric: false,
    });
    
    // Track which fields have been touched (focused and blurred) - for on-blur validation
    const [touchedFields, setTouchedFields] = useState(new Set());
    
    // Helper to mark field as touched
    const markFieldTouched = (fieldKey) => {
        setTouchedFields(prev => new Set(prev).add(fieldKey));
    };


    // Email verification state
    const [emailVerified, setEmailVerified] = useState(false);
    const [isSendingVerification, setIsSendingVerification] = useState(false);
    const [emailVerificationSent, setEmailVerificationSent] = useState(false);
    const [originalEmail, setOriginalEmail] = useState(''); // Track email changes

    // Enrollment policy for the selected cert run
    const [policyData, setPolicyData] = useState(null);

    const getAllCourseRuns = async () => {
        setLoadingCourseRuns(true);
        let basequery = supabase.from("full_certification_runs")
        .select("*, full_cert_many_course_run(*, course_run(*, course_type(*))), full_certification(*)")
            .eq("is_active", true)
            .order("start_date");

        if (fullCertificationId) {
            // full_certification_runs table uses full_cert_type_code to reference full_certification.code
            basequery = basequery.eq('full_cert_type_code', fullCertificationId);
        }

        const {data, error} = await basequery;

        if (error) {
            console.error("Error fetching course runs:", error);
            setLoadingCourseRuns(false);
            return;
        }

        // Filter runs that are still accepting registrations
        // Show runs where registration_closing_date >= today OR (registration_closing_date is null AND start_date >= today)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const filteredData = (data || []).filter(certRun => {
            // If registration_closing_date exists, use it
            if (certRun.registration_closing_date) {
                const closingDate = new Date(certRun.registration_closing_date);
                closingDate.setHours(0, 0, 0, 0);
                return closingDate >= today;
            }
            // Otherwise, fall back to start_date
            if (certRun.start_date) {
                const startDate = new Date(certRun.start_date);
                startDate.setHours(0, 0, 0, 0);
                return startDate >= today;
            }
            // If neither exists, include it (shouldn't happen but be safe)
            return true;
        });

        // Extract all course_run IDs from the nested structure
        const courseRunIds = [];
        filteredData.forEach(certRun => {
            if (certRun.full_cert_many_course_run) {
                certRun.full_cert_many_course_run.forEach(link => {
                    if (link.course_run && link.course_run.id) {
                        courseRunIds.push(link.course_run.id);
                    }
                });
            }
        });

        // Fetch all sessions for these course runs
        let sessionsData = [];
        if (courseRunIds.length > 0) {
            const { data: allSessions, error: sessionsError } = await supabase
                .from("session")
                .select("*")
                .in("course_run_code", courseRunIds)
                .order("session_date", { ascending: true });

            if (sessionsError) {
                console.error("Error fetching sessions:", sessionsError);
            } else {
                sessionsData = allSessions || [];
            }
        }

        // Group sessions by course_run_code
        const sessionsByCourseRun = {};
        sessionsData.forEach(session => {
            if (!sessionsByCourseRun[session.course_run_code]) {
                sessionsByCourseRun[session.course_run_code] = [];
            }
            sessionsByCourseRun[session.course_run_code].push(session);
        });

        // Transform data to include course_dates from sessions
        const transformedData = (data || []).map(certRun => {
            // Process nested course_run data
            if (certRun.full_cert_many_course_run) {
                certRun.full_cert_many_course_run = certRun.full_cert_many_course_run.map(link => {
                    if (link.course_run) {
                        const sessions = sessionsByCourseRun[link.course_run.id] || [];
                        const course_dates = sessions.map(session => ({
                            date: session.session_date,
                            start_time: session.start_time ? moment(session.start_time, "HH:mm:ss").format("HHmm") : null,
                            end_time: session.end_time ? moment(session.end_time, "HH:mm:ss").format("HHmm") : null,
                            room: session.custom_room || session.room_id,
                            venue: session.custom_venue || session.venue_id
                        }));
                        link.course_run.course_dates = course_dates;
                    }
                    return link;
                });
            }
            return certRun;
        });

        // Group by certification type and sort by start_date
        const groupedByCertType = transformedData.reduce((acc, certRun) => {
            const certTypeKey = certRun.full_certification?.code || certRun.full_cert_type_code || 'unknown';
            const certTypeName = certRun.full_certification?.name || 'Unknown Certification';
            
            if (!acc[certTypeKey]) {
                acc[certTypeKey] = {
                    name: certTypeName,
                    runs: []
                };
            }
            
            acc[certTypeKey].runs.push(certRun);
            return acc;
        }, {});

        // Sort each group's runs by start_date ascending
        Object.keys(groupedByCertType).forEach(certTypeKey => {
            groupedByCertType[certTypeKey].runs.sort((a, b) => {
                const dateA = new Date(a.start_date);
                const dateB = new Date(b.start_date);
                return dateA - dateB;
            });
            
            // Filter to show max 3 upcoming runs + ongoing runs within 2 weeks of start date
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            const twoWeeksAgo = new Date(today);
            twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
            
            const upcomingRuns = [];
            const ongoingRuns = [];
            
            groupedByCertType[certTypeKey].runs.forEach(run => {
                if (!run.start_date) return;
                
                const startDate = new Date(run.start_date);
                startDate.setHours(0, 0, 0, 0);
                
                // Ongoing: started within last 2 weeks (start_date <= today AND start_date >= today - 14 days)
                if (startDate <= today && startDate >= twoWeeksAgo) {
                    ongoingRuns.push(run);
                }
                // Upcoming: start_date > today
                else if (startDate > today && upcomingRuns.length < 3) {
                    upcomingRuns.push(run);
                }
            });
            
            // Combine: ongoing first, then upcoming (both already sorted by start_date)
            groupedByCertType[certTypeKey].runs = [...ongoingRuns, ...upcomingRuns];
        });

        // Store both the grouped structure and flat list for backward compatibility
        setCourseRuns(transformedData);
        setGroupedCourseRuns(groupedByCertType);
        devLog("Fetched course run data:", transformedData);
        devLog("Grouped course runs:", groupedByCertType);
        setLoadingCourseRuns(false);
    };

    // Load existing registration if ID is in URL (and merge locally-saved draft so refresh won't wipe Step 2)
    useEffect(() => {
        // Reset toast flag when ID changes
        hasShownLoadToast.current = false;

        const loadRegistration = async () => {
            if (!id) return;
            setLoadingRegistration(true);
            try {
                const { data, error } = await fetchPublicRegistration(supabase, id);

                if (error) throw error;

                if (data) {
                    // Populate form with existing data including additional_data
                        const baseFormData = {
                        participant_name: data.participant_name || '',
                        participant_email: data.participant_email || '',
                        nationality: data.nationality || '',
                        birth_age: data.birth_age || '',
                        nric: data.nric || '',
                        mobile_no: data.mobile_no || '',
                        mobile_no_raw: data.mobile_no || '',
                        dob: data.dob || '',
                        displayDob: data.dob || '',
                        draft_cert_run_code: data.draft_cert_run_code || '',
                        rejection_reason: data.rejection_reason || '',
                        registration_stage: data.registration_stage || '',
                    };

                    const mergedFormData = data.additional_data
                        ? { ...baseFormData, ...data.additional_data }
                        : baseFormData;

                    const localDraft = readLocalDraft();

                    // If TMS has moved the registration past draft, never let a stale local draft override server state.
                    const serverStage = data.registration_stage || '';
                    const isServerPastDraft = serverStage && serverStage !== 'draft';

                    let mergedWithLocalDraft = mergedFormData;
                    if (localDraft?.formData && !isServerPastDraft) {
                        // Only apply local draft while still in draft stage
                        mergedWithLocalDraft = { ...mergedFormData, ...localDraft.formData };
                    } else if (localDraft?.formData && isServerPastDraft) {
                        // Clear stale local draft so it won't keep overriding on future refreshes
                        clearLocalDraft();
                    }

                    // Always trust server for registration stage
                    mergedWithLocalDraft.registration_stage = serverStage;

                    setFormData(mergedWithLocalDraft);
                    setRegistrationId(id);

                    // Load email verification status
                    setEmailVerified(data.email_verified || false);
                    setEmailVerificationSent(!!data.email_verification_sent_at);
                    setOriginalEmail(data.participant_email || '');

                    if (!hasShownLoadToast.current) {
                        hasShownLoadToast.current = true;
                        setTimeout(() => {
                            toast({
                                title: "Registration Loaded",
                                description: "Your saved registration has been loaded.",
                                status: "info",
                                duration: 2000,
                                isClosable: true,
                            });
                        }, 100);
                    }
                }
            } catch (err) {
                console.error('Error loading registration:', err);
                toast({
                    title: "Error Loading Registration",
                    description: "Could not load the saved registration. Please try again.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            } finally {
                setLoadingRegistration(false);
            }
        };

        loadRegistration();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Debounced auto-save to localStorage (so refresh keeps Step 2 inputs)
    useEffect(() => {
        if (!localDraftKey) return;
        if (!(formData.participant_name || formData.participant_email || formData.nric)) return;

        const t = setTimeout(() => {
            writeLocalDraft(formData);
        }, 600);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localDraftKey, formData]);

    // Fetch course runs from database
    useEffect(() => {
        getAllCourseRuns();
    }, []);

    // Resolve registration template when the selected cert run changes.
    // Priority: full_certification_runs.registration_template_id → full_certification.registration_template_id → all fields
    useEffect(() => {
        const resolveTemplate = async () => {
            if (!formData.draft_cert_run_code || !courseRuns.length) return;

            const selectedRun = courseRuns.find(c => c.id === formData.draft_cert_run_code);
            const templateId =
                selectedRun?.registration_template_id ||
                selectedRun?.full_certification?.registration_template_id;

            if (!templateId) {
                setTemplateFieldIds(null);
                return;
            }

            try {
                const { data, error } = await supabase
                    .from('registration_templates')
                    .select('field_ids')
                    .eq('id', templateId)
                    .single();

                if (error) throw error;

                const ids = (data?.field_ids || []).map(f => f.field_id);
                setTemplateFieldIds(ids);
            } catch (err) {
                console.error('Error fetching registration template:', err);
                setTemplateFieldIds(null);
            }
        };

        resolveTemplate();
    }, [formData.draft_cert_run_code, courseRuns]);

    // Fetch organization GST settings
    useEffect(() => {
        const fetchOrganizationGstSettings = async () => {
            try {
                const { data, error } = await supabase
                    .from('organization')
                    .select('gst_enabled, gst_rate')
                    .eq('status', 'active')
                    .single();
                
                if (error) {
                    console.error('Error fetching organization GST settings:', error);
                    return;
                }
                
                if (data) {
                    setOrgGstSettings({
                        gst_enabled: data.gst_enabled || false,
                        gst_rate: parseFloat(data.gst_rate) || 0
                    });
                    devLog('Organization GST settings loaded:', data);
                }
            } catch (err) {
                console.error('Error fetching organization GST settings:', err);
            }
        };
        
        fetchOrganizationGstSettings();
    }, []);

    // Initialize selectedCertName when course runs are loaded or when cert run is already selected
    useEffect(() => {
        if (Object.keys(groupedCourseRuns).length > 0) {
            // If fullCertificationId is in URL params, pre-select it
            if (fullCertificationId && !selectedCertName) {
                // Check if the fullCertificationId matches any cert type key
                const matchingCertKey = Object.keys(groupedCourseRuns).find(
                    key => key === fullCertificationId
                );
                if (matchingCertKey) {
                    setSelectedCertName(matchingCertKey);
                }
            }
            
            // If draft_cert_run_code is already set, find the corresponding certification
            if (formData.draft_cert_run_code && !selectedCertName) {
                const selectedRun = courseRuns.find(c => c.id === formData.draft_cert_run_code);
                if (selectedRun) {
                    const certKey = selectedRun.full_certification?.code || selectedRun.full_cert_type_code;
                    if (certKey && groupedCourseRuns[certKey]) {
                        setSelectedCertName(certKey);
                    }
                }
            }
        }
    }, [groupedCourseRuns, courseRuns, fullCertificationId, formData.draft_cert_run_code]);

    // Auto-select passport option when E-Learning course is selected
    useEffect(() => {
        if (!basicFieldsOrder.length) return; // Wait for basic fields to load
        
        const fieldConfig = basicFieldsOrder.find(f => f.field_key === 'nationality');
        const allOptions = (fieldConfig?.field_options && fieldConfig.field_options.length > 0) ? fieldConfig.field_options : DEFAULT_NATIONALITY_OPTION_VALUES;
        
        if (isElearningCourse()) {
            const passportOption = getFirstPassportOption(allOptions);
            if (passportOption) {
                // Auto-select passport if not already selected or if current selection is not passport
                if (!formData.nationality || !requiresPassport(formData.nationality)) {
                    handleChange("nationality", passportOption);
                    if (!requiresPassport(formData.nationality)) {
                        handleChange("nric", ''); // Clear NRIC since passport is selected
                    }
                }
            }
        }
    }, [selectedCertName, formData.draft_cert_run_code, basicFieldsOrder, groupedCourseRuns, formData.nationality]);

    // Fetch full certification cost
    useEffect(() => {
        const fetchFullCertificationCost = async () => {
            const params = new URLSearchParams(location.search);
            const fullCertificationId = params.get('full_certification');

            if (fullCertificationId && !fullCertificationCost) {
                try {
                    const {data, error} = await supabase
                        .from('full_certification')
                        .select('cost')
                        .eq('code', fullCertificationId)
                        .single();

                    if (error) throw error;
                    setFullCertificationCost(parseFloat(data.cost || 0));
                } catch (err) {
                    console.error('Error fetching full certification cost:', err);
                }
            }
        };

        fetchFullCertificationCost();
    }, [location.search, fullCertificationCost]);

    // Auto-select all modules when certification run is selected
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const fullCertification = params.get('full_certification');

        if (fullCertification && formData.certification_run_id && courseRuns.length > 0) {
            // courseRuns contains full_certification_runs records, which have full_cert_type_code referencing full_certification.code
            const certificationCourses = courseRuns.filter(course =>
                (course.full_cert_type_code === fullCertification || course.full_certification?.code === fullCertification) &&
                course.certification_run_id === formData.certification_run_id &&
                course.is_full_certification === true
            );

            devLog('Found certification courses:', certificationCourses.length, certificationCourses);

            if (certificationCourses.length > 0) {
                const allCourseRunIds = certificationCourses.map(course => course.id);
                setSelectedCourseRunIds(allCourseRunIds);
                devLog('Auto-selected course run IDs:', allCourseRunIds);
            }
        }
    }, [formData.certification_run_id, courseRuns, location.search]);

    // Fetch additional fields for validation
    useEffect(() => {
        const fetchAdditionalFields = async () => {
            try {
                const { data, error } = await supabase
                    .from('additional_registration_fields')
                    .select('field_key, is_required, field_label, conditional_requirements, field_type')
                    .or('field_status.is.null,field_status.eq.active');
                
                if (error) throw error;
                const normalized = (data || []).map((row) => ({
                    ...row,
                    conditional_requirements: normalizeConditionalRequirements(row.conditional_requirements),
                }));
                setAdditionalFields(normalized);
            } catch (err) {
                console.error('Error fetching additional fields:', err);
            }
        };
        
        fetchAdditionalFields();
    }, []);

    // Fetch basic fields order from database (system fields)
    useEffect(() => {
        const fetchBasicFieldsOrder = async () => {
            try {
                const { data, error } = await supabase
                    .from('additional_registration_fields')
                    .select('id, field_key, field_label, display_order, field_behaviors, field_options, field_type, is_required, is_hidden')
                    .or('field_status.is.null,field_status.eq.active')
                    .eq('is_original_field', true)
                    .order('display_order', { ascending: true });
                
                if (error) throw error;
                
                // Parse field_behaviors if it's a string
                const processedData = (data || []).map(field => {
                    let fieldBehaviors = field.field_behaviors;
                    if (typeof fieldBehaviors === 'string') {
                        try {
                            fieldBehaviors = JSON.parse(fieldBehaviors);
                        } catch (e) {
                            console.warn('Failed to parse field_behaviors for field:', field.field_key, e);
                            fieldBehaviors = null;
                        }
                    }
                    return {
                        ...field,
                        field_behaviors: fieldBehaviors
                    };
                });
                
                setBasicFieldsOrder(processedData);
            } catch (err) {
                console.error('Error fetching basic fields order:', err);
            }
        };
        
        fetchBasicFieldsOrder();
    }, []);

    // Fetch compulsory custom fields (custom fields in Compulsory fields section) for Step 1
    useEffect(() => {
        const fetchCompulsoryCustomFields = async () => {
            try {
                const { data, error } = await supabase
                    .from('additional_registration_fields')
                    .select('*')
                    .or('field_status.is.null,field_status.eq.active')
                    .eq('is_original_field', false) // Only custom fields
                    .eq('section', 'Compulsory fields')
                    .order('display_order', { ascending: true });
                
                if (error) throw error;
                
                // Parse field_behaviors if it's a string
                const processedData = (data || []).map(field => {
                    let fieldBehaviors = field.field_behaviors;
                    if (typeof fieldBehaviors === 'string') {
                        try {
                            fieldBehaviors = JSON.parse(fieldBehaviors);
                        } catch (e) {
                            console.warn('Failed to parse field_behaviors for field:', field.field_key, e);
                            fieldBehaviors = null;
                        }
                    }
                    return {
                        ...field,
                        field_behaviors: fieldBehaviors
                    };
                });
                
                setCompulsoryCustomFields(processedData);
            } catch (err) {
                console.error('Error fetching compulsory custom fields:', err);
            }
        };
        
        fetchCompulsoryCustomFields();
    }, []);

    // Set document title
    useEffect(() => {
        document.title = "Full Certification Registration - Edthos Learning";
        return () => {
            document.title = "Edthos Learning";
        };
    }, []);

    // Helper function to get dynamic label based on field_behaviors
    const getOriginalFieldConfig = (fieldKey) => basicFieldsOrder.find(f => f.field_key === fieldKey);

    const isSystemFieldInTemplate = (fieldKey) => {
        if (templateFieldIds === null) return true;
        const config = getOriginalFieldConfig(fieldKey);
        if (!config?.id) return true;
        return templateFieldIds.includes(config.id);
    };

    const fieldInTemplate = (fieldKey) => {
        if (templateFieldIds === null) return true;
        const config = getOriginalFieldConfig(fieldKey);
        return !config?.id || templateFieldIds.includes(config.id);
    };

    const countryOptions = useMemo(() => countryList().getData(), []);

    const getDynamicLabel = (fieldKey, defaultLabel) => {
        // Find the field in basicFieldsOrder or compulsoryCustomFields
        const field = basicFieldsOrder.find(f => f.field_key === fieldKey) || 
                     compulsoryCustomFields.find(f => f.field_key === fieldKey);
        if (!field?.field_behaviors?.dynamic_label) {
            return defaultLabel;
        }

        const { trigger_field, mappings } = field.field_behaviors.dynamic_label;
        const triggerValue = formData[trigger_field] || '';
        
        return mappings[triggerValue] || mappings.default || defaultLabel;
    };

    // Helper function to get dynamic label for a field object
    const getFieldDynamicLabel = (field) => {
        if (!field?.field_behaviors?.dynamic_label) {
            return field.field_label;
        }

        const { trigger_field, mappings } = field.field_behaviors.dynamic_label;
        const triggerValue = getFieldValue(formData, trigger_field) || '';
        
        return mappings[triggerValue] || mappings.default || field.field_label;
    };

    // Helper function to get placeholder (supports both static and dynamic)
    const getFieldPlaceholder = (field) => {
        if (!field || !field.field_behaviors) {
            return '';
        }
        
        // First check for static custom placeholder
        if (field.field_behaviors.custom_placeholder) {
            return field.field_behaviors.custom_placeholder;
        }
        
        // Then check for dynamic placeholder from dynamic_label.placeholders
        if (field.field_behaviors.dynamic_label?.placeholders) {
            const { trigger_field, placeholders } = field.field_behaviors.dynamic_label;
            const triggerValue = getFieldValue(formData, trigger_field) || '';
            
            // Try to get placeholder for current trigger value, fallback to default
            const placeholder = placeholders[triggerValue] || placeholders.default || '';
            return placeholder;
        }
        
        // Fallback to empty string (no placeholder)
        return '';
    };

    // REMOVED: Scroll-into-view for verification banner - it was jarring on mobile
    // Instead, we now show a fixed notification toast and the banner stays in its natural position
    // The user can continue filling the form without being forcibly scrolled

    const handleChange = (name, value) => {
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    // Helper function to check if nationality is Singapore Citizen/PR (supports both old and new values)
    const isSingaporeCitizenOrPR = (nationality) => {
        if (!nationality) return false;
        return nationality === "Singapore Citizen" || 
               nationality === "Singapore Permanent Resident" ||
               nationality === "Singapore Citizen / PR";
    };

    // Helper function to check if nationality requires passport (supports both old and new values)
    const requiresPassport = (nationality) => {
        if (!nationality) return false;
        return nationality === "Non-Singapore Resident (Foreign Passport)" ||
               nationality === "Non-Singapore Resident( Passport No)";
    };

    // Helper function to check if nationality requires FIN (backend values only; frontend maps display→backend)
    const requiresFIN = (nationality) => {
        if (!nationality) return false;
        return nationality === "Work Pass/ E-Pass / LTVP (FIN)" ||
               nationality === "Non-Singapore Citizen/PR (FIN)" ||
               nationality === "Long Term Visitor Pass+ (LTVP+)";
    };

    // Helper function to check if selected course is E-Learning
    const isElearningCourse = () => {
        // Check from selected certification name (most reliable source)
        if (selectedCertName && groupedCourseRuns[selectedCertName]) {
            const certName = groupedCourseRuns[selectedCertName].name || '';
            if (certName && certName.includes('(E-Learning)')) {
                return true;
            }
        }
        
        // Fallback: Check from selected course run
        if (formData.draft_cert_run_code) {
            const selectedRun = courseRuns.find(c => c.id === formData.draft_cert_run_code);
            if (selectedRun) {
                // Check full certification name first, then course type name
                const courseName = selectedRun?.full_certification?.name || 
                                  selectedRun?.course_type?.name || 
                                  '';
                if (courseName && courseName.includes('(E-Learning)')) {
                    return true;
                }
            }
        }
        
        return false;
    };

    // Helper function to get the first passport option from available options
    const getFirstPassportOption = (allOptions) => {
        const passportOptions = allOptions.filter(option => 
            option.includes('Passport') || 
            option.includes('passport') ||
            option === "Non-Singapore Resident( Passport No)" ||
            option === "Non-Singapore Resident (Foreign Passport)"
        );
        return passportOptions.length > 0 ? passportOptions[0] : null;
    };

    // Helper function to filter nationality options for E-Learning courses (only passport)
    const getFilteredNationalityOptions = (allOptions) => {
        if (!isElearningCourse()) {
            return allOptions; // Return all options if not E-Learning
        }
        
        // For E-Learning courses, only return passport-related options
        const passportOptions = allOptions.filter(option => 
            option.includes('Passport') || 
            option.includes('passport') ||
            option === "Non-Singapore Resident( Passport No)" ||
            option === "Non-Singapore Resident (Foreign Passport)"
        );
        
        return passportOptions.length > 0 ? passportOptions : allOptions; // Fallback to all if no passport options found
    };

    // Render basic field based on field_key
    const renderBasicField = (fieldKey) => {
        if (!isSystemFieldInTemplate(fieldKey)) return null;

        // Find the field configuration from basicFieldsOrder
        const fieldConfig = basicFieldsOrder.find(f => f.field_key === fieldKey);
        if (fieldConfig?.is_hidden) return null;

        const isFieldRequired = fieldConfig ? fieldConfig.is_required !== false : true;
        
        const fieldMap = {
            'nationality': (
                <FormControl key="nationality" mb={{base: 4, md: 5}}>
                    <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                        <BilingualLabel
                            label={fieldConfig?.field_label || "Identification Type"}
                            isRequired={isFieldRequired}
                        />
                    </FormLabel>
                    {(() => {
                        if (fieldConfig?.field_type === 'country') {
                            const currentVal = countryOptions.find(
                                opt => opt.label === formData.nationality || opt.value === formData.nationality
                            ) || null;
                            return (
                                <RegistrationSearchableSelect
                                    value={currentVal}
                                    onChange={(option) => {
                                        handleChange('nationality', option ? option.label : '');
                                    }}
                                    options={countryOptions}
                                    placeholder="Search and select country..."
                                    isClearable
                                    isDisabled={isReadOnly}
                                />
                            );
                        }

                        const rawOptions = (fieldConfig?.field_options && fieldConfig.field_options.length > 0) ? fieldConfig.field_options : DEFAULT_NATIONALITY_OPTION_VALUES;
                        const optionLabels = fieldConfig?.field_behaviors?.option_labels ?? {};
                        const filteredOptions = getFilteredNationalityOptions(rawOptions);
                        const getDisplayLabel = (value) => optionLabels[value] ?? value;

                        if (filteredOptions.length > 0 && filteredOptions.length < 5) {
                            return (
                                <SimpleGrid 
                                    columns={{ 
                                        base: 1, 
                                        sm: filteredOptions.length === 2 ? 2 : (filteredOptions.length === 3 ? 3 : 2),
                                        md: Math.min(filteredOptions.length, 3)
                                    }} 
                                    spacing={2}
                                >
                                    {filteredOptions.map((value) => {
                                        const displayLabel = getDisplayLabel(value);
                                        const isSelected = formData.nationality === value;
                                        return (
                                            <Button
                                                key={value}
                                                onClick={() => {
                                                    handleChange("nationality", value);
                                                    if (isElearningCourse() && !requiresPassport(value)) {
                                                        handleChange("nric", '');
                                                    }
                                                }}
                                                colorScheme={isSelected ? "blue" : "gray"}
                                                variant={isSelected ? "solid" : "outline"}
                                                isDisabled={isReadOnly}
                                                size="md"
                                                py={{ base: 4, md: 5 }}
                                                px={4}
                                                whiteSpace="normal"
                                                height="auto"
                                                textAlign="center"
                                                fontWeight={isSelected ? "bold" : "medium"}
                                                borderColor={isSelected ? "blue.500" : "gray.300"}
                                                _hover={!isReadOnly ? {
                                                    bg: isSelected ? "blue.600" : "gray.100",
                                                    borderColor: "blue.500"
                                                } : {}}
                                            >
                                                {displayLabel}
                                            </Button>
                                        );
                                    })}
                                </SimpleGrid>
                            );
                        }

                        return (
                            <Select
                                value={formData.nationality}
                                placeholder='Select option'
                                bg={isReadOnly ? "gray.50" : "white"}
                                onChange={(e) => {
                                    handleChange("nationality", e.target.value);
                                    if (isElearningCourse() && !requiresPassport(e.target.value)) {
                                        handleChange("nric", '');
                                    }
                                }}
                                isDisabled={isReadOnly}
                                size={{base: "md", md: "md"}}
                                height={{base: "48px", md: "40px"}}
                            >
                                {filteredOptions.map((value) => (
                                    <option key={value} value={value}>{getDisplayLabel(value)}</option>
                                ))}
                            </Select>
                        );
                    })()}
                </FormControl>
            ),
            'nric': (
                <FormControl key="nric" mb={{base: 4, md: 5}}>
                    <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                        <BilingualLabel
                            label={getDynamicLabel('nric', fieldConfig?.field_label || 'NRIC Number')}
                            isRequired={isFieldRequired}
                        />
                    </FormLabel>
                    <Input
                        value={formData.nric || ''}
                        onChange={(e) => {
                            const nric = isSingaporeCitizenOrPR(formData.nationality) ? e.target.value.toUpperCase() : e.target.value;
                            handleChange("nric", nric);
                        }}
                        isReadOnly={isReadOnly}
                        bg={isReadOnly ? "gray.50" : "white"}
                        onBlur={(e) => {
                            markFieldTouched('nric');
                            const nric = e.target.value;
                            const isValid = validateNRIC(nric, formData.nationality);
                            const isSG = isSingaporeCitizenOrPR(formData.nationality);
                            
                            setValidationErrors(prev => ({
                                ...prev,
                                nric: !isValid && nric !== "" 
                                    ? (isSG 
                                        ? "Invalid NRIC / NRIC format" 
                                        : (requiresPassport(formData.nationality)
                                            ? "Please enter your passport number"
                                            : "Please enter your FIN number"))
                                    : false
                            }));
                        }}
                        placeholder={(() => {
                            // Get placeholder from field_behaviors (dynamic or static)
                            const fieldConfig = basicFieldsOrder.find(f => f.field_key === 'nric');
                            if (fieldConfig) {
                                const placeholder = getFieldPlaceholder(fieldConfig);
                                if (placeholder) return placeholder;
                            }
                            // Fallback to hardcoded logic if no database placeholder
                            return requiresPassport(formData.nationality) ? "Enter passport number" :
                                   requiresFIN(formData.nationality) ? "F1234567X" :
                                   "S9912345Z";
                        })()}
                        size={{base: "lg", md: "md"}}
                        height={{base: "48px", md: "40px"}}
                        type="text"
                        maxLength={isSingaporeCitizenOrPR(formData.nationality) || requiresFIN(formData.nationality) ? 9 : undefined}
                        isInvalid={touchedFields.has('nric') && validationErrors.nric}
                    />
                    {touchedFields.has('nric') && validationErrors.nric && (
                        <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                            {validationErrors.nric}
                        </Text>
                    )}
                </FormControl>
            ),
            'dob': (
                <FormControl key="dob" mb={{base: 4, md: 5}}>
                    <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                        <BilingualLabel
                            label={fieldConfig?.field_label || 'Date of Birth'}
                            isRequired={isFieldRequired}
                        />
                    </FormLabel>
                    <Input
                        value={formData.displayDob || ''}
                        onChange={(e) => {
                            const inputDate = e.target.value;
                            handleChange("displayDob", inputDate);

                            const date = new Date(inputDate);
                            if (!isNaN(date)) {
                                const storedFormat = date.toISOString().split('T')[0];
                                const age = calculateAge(storedFormat);
                                handleChange("dob", storedFormat);
                                handleChange("birth_age", age);
                            }
                        }}
                        isReadOnly={isReadOnly}
                        bg={isReadOnly ? "gray.50" : "white"}
                        onBlur={(e) => {
                            markFieldTouched('dob');
                            const inputDate = e.target.value;
                            if (!inputDate) {
                                setValidationErrors(prev => ({
                                    ...prev,
                                    dob: "Date of birth is required"
                                }));
                                return;
                            }

                            const date = new Date(inputDate);
                            if (isNaN(date)) {
                                setValidationErrors(prev => ({...prev, dob: "Invalid date"}));
                                return;
                            }

                            const age = calculateAge(date);
                            if (age < 0 || age > 150) {
                                setValidationErrors(prev => ({...prev, dob: "Invalid date of birth"}));
                                return;
                            }

                            // Catalog minimum age (full_certification.minimum_age); blank/null = no restriction
                            const certRun = courseRuns.find(c => c.id === formData.draft_cert_run_code);
                            const minAgeRaw = certRun?.full_certification?.minimum_age;
                            const minAge = minAgeRaw != null && minAgeRaw !== '' ? Number(minAgeRaw) : null;
                            if (minAge != null && Number.isFinite(minAge) && age < minAge) {
                                setValidationErrors(prev => ({
                                    ...prev,
                                    dob: `You must be at least ${minAge} years old to register`
                                }));
                                return;
                            }

                            setValidationErrors(prev => ({...prev, dob: false}));
                        }}
                        placeholder={(() => {
                            // Get placeholder from field_behaviors
                            const fieldConfig = basicFieldsOrder.find(f => f.field_key === 'dob');
                            if (fieldConfig) {
                                const placeholder = getFieldPlaceholder(fieldConfig);
                                if (placeholder) return placeholder;
                            }
                            // Fallback
                            return "Select date";
                        })()}
                        size={{base: "lg", md: "md"}}
                        height={{base: "48px", md: "40px"}}
                        type="date"
                        isInvalid={touchedFields.has('dob') && validationErrors.dob}
                    />
                    {touchedFields.has('dob') && validationErrors.dob && (
                        <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                            {validationErrors.dob}
                        </Text>
                    )}
                </FormControl>
            ),
            'participant_name': (
                <FormControl key="participant_name" mb={{base: 4, md: 5}}>
                    <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                        <BilingualLabel
                            label={getDynamicLabel('participant_name', fieldConfig?.field_label || 'Full Name as Per NRIC')}
                            isRequired={isFieldRequired}
                        />
                    </FormLabel>
                    <Input
                        value={formData.participant_name || ''}
                        onChange={(e) => handleChange("participant_name", e.target.value)}
                        isReadOnly={isReadOnly}
                        bg={isReadOnly ? "gray.50" : "white"}
                        size={{base: "lg", md: "md"}}
                        height={{base: "48px", md: "40px"}}
                        type="text"
                        placeholder={(() => {
                            // Get placeholder from field_behaviors
                            const nameFieldConfig = basicFieldsOrder.find(f => f.field_key === 'participant_name');
                            if (nameFieldConfig) {
                                const placeholder = getFieldPlaceholder(nameFieldConfig);
                                if (placeholder) return placeholder;
                            }
                            // Fallback
                            return '';
                        })()}
                    />
                </FormControl>
            ),
            'participant_email': (
                <FormControl key="participant_email" mb={{base: 4, md: 5}} isInvalid={touchedFields.has('email') && (validationErrors.email || validationErrors.invalidDomain)}>
                    <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                        <BilingualLabel
                            label={fieldConfig?.field_label || 'Participant Email'}
                            isRequired={isFieldRequired}
                        />
                        {emailVerified && (
                            <Badge ml={2} colorScheme="green" fontSize="xs">
                                ✓ Verified
                            </Badge>
                        )}
                    </FormLabel>
                    <Input
                        value={formData.participant_email || ''}
                        placeholder={(() => {
                            // Get placeholder from field_behaviors
                            const emailFieldConfig = basicFieldsOrder.find(f => f.field_key === 'participant_email');
                            if (emailFieldConfig) {
                                const placeholder = getFieldPlaceholder(emailFieldConfig);
                                if (placeholder) return placeholder;
                            }
                            // Fallback
                            return '';
                        })()}
                        onChange={(e) => {
                            const newEmail = e.target.value;
                            
                            // Prevent email changes if already verified (better UX)
                            if (emailVerified && registrationId) {
                                toast({
                                    title: "Email Already Verified",
                                    description: "Email cannot be changed after verification. Please contact support if you need to change your email.",
                                    status: "warning",
                                    duration: 5000,
                                    isClosable: true,
                                });
                                return;
                            }
                            
                            handleChange("participant_email", newEmail);
                            
                            // If email changed and we have a registration ID, invalidate verification
                            if (registrationId && originalEmail && newEmail !== originalEmail) {
                                setEmailVerified(false);
                                setEmailVerificationSent(false);
                            }
                        }}
                        onBlur={async (e) => {
                            const newEmail = e.target.value;
                            
                            // If email changed and registration is saved, send new verification
                            if (registrationId && originalEmail && newEmail !== originalEmail && newEmail) {
                                // Update original email
                                setOriginalEmail(newEmail);
                                
                                // IMPORTANT: Update email in database immediately
                                // This ensures verification works correctly
                                const { error: updateError } = await supabase
                                    .from('registration')
                                    .update({ 
                                        participant_email: newEmail, // Update email in database
                                        email_verified: false, 
                                        email_verification_token: null,
                                        email_verification_token_expires_at: null
                                    })
                                    .eq('id', registrationId);
                                
                                if (updateError) {
                                    console.error('Error updating email:', updateError);
                                    toast({
                                        title: "Error Updating Email",
                                        description: "Failed to update email. Please try again.",
                                        status: "error",
                                        duration: 5000,
                                        isClosable: true,
                                    });
                                    return;
                                }
                                
                                // Send new verification email
                                await sendVerificationEmail(
                                    registrationId,
                                    newEmail,
                                    formData.participant_name
                                );
                            }
                            
                            // Mark as touched and validate
                            markFieldTouched('email');
                            const email = e.target.value;
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            const hasInvalidDomain = /\.\w$/.test(email);

                            setValidationErrors(prev => ({
                                ...prev,
                                email: !emailRegex.test(email) && email !== "",
                                invalidDomain: hasInvalidDomain
                            }));
                        }}
                        isReadOnly={isReadOnly}
                        bg={isReadOnly ? "gray.50" : "white"}
                        size={{base: "lg", md: "md"}}
                        height={{base: "48px", md: "40px"}}
                        type="email"
                        isInvalid={touchedFields.has('email') && (validationErrors.email || validationErrors.invalidDomain)}
                    />
                    {touchedFields.has('email') && validationErrors.email && (
                        <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                            Please enter a valid email address
                        </Text>
                    )}
                    {touchedFields.has('email') && validationErrors.invalidDomain && (
                        <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                            Invalid domain - domain must be at least 2 characters
                        </Text>
                    )}
                </FormControl>
            ),
            'mobile_no': (
                <FormControl key="mobile_no" mb={{base: 4, md: 5}} isInvalid={touchedFields.has('mobile') && validationErrors.mobile}>
                    <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                        <BilingualLabel
                            label={fieldConfig?.field_label || 'Mobile Number'}
                            isRequired={isFieldRequired}
                        />
                    </FormLabel>
                    <PhoneInput
                        international
                        defaultCountry="SG"
                        disabled={isReadOnly}
                        value={formData.mobile_no || ''}
                        onChange={(value) => {
                            handleChange("mobile_no", value || '');
                            // Only validate if field has been touched
                            if (touchedFields.has('mobile')) {
                                if (value) {
                                    try {
                                        const isValid = isValidPhone(value);
                                        setValidationErrors(prev => ({
                                            ...prev,
                                            mobile: !isValid
                                        }));
                                        if (isValid) {
                                            const phoneNumber = parsePhone(value);
                                            const formattedNumber = phoneNumber.format('INTERNATIONAL');
                                            const numberOnly = phoneNumber.nationalNumber;
                                            setFormData(prevData => ({
                                                ...prevData,
                                                mobile_no: formattedNumber,
                                                mobile_no_raw: numberOnly
                                            }));
                                        }
                                    } catch (error) {
                                        setValidationErrors(prev => ({...prev, mobile: true}));
                                    }
                                } else {
                                    setValidationErrors(prev => ({...prev, mobile: false}));
                                }
                            }
                        }}
                        onBlur={() => {
                            markFieldTouched('mobile');
                            // Validate on blur
                            const value = formData.mobile_no;
                            if (value) {
                                try {
                                    const isValid = isValidPhone(value);
                                    setValidationErrors(prev => ({...prev, mobile: !isValid}));
                                } catch (error) {
                                    setValidationErrors(prev => ({...prev, mobile: true}));
                                }
                            }
                        }}
                        inputComponent={Input}
                        style={{
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        className="chakra-phone-input"
                        inputProps={{
                            isReadOnly: isReadOnly,
                            bg: isReadOnly ? "gray.50" : "white",
                            size: {base: "lg", md: "md"},
                            height: {base: "48px", md: "40px"},
                            isInvalid: touchedFields.has('mobile') && validationErrors.mobile
                        }}
                    />
                    {touchedFields.has('mobile') && validationErrors.mobile && (
                        <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                            Please enter a valid mobile number
                        </Text>
                    )}
                </FormControl>
            )
        };

        return fieldMap[fieldKey] || null;
    };

    // Render compulsory custom field (custom fields in Compulsory fields section) for Step 1
    const renderCompulsoryCustomField = (field) => {
        if (templateFieldIds !== null && field.id != null && !templateFieldIds.includes(field.id)) {
            return null;
        }
        if (field.is_hidden) return null;

        const fieldValue = getFieldValue(formData, field.field_key);
        // Only show error if field has been touched
        const isTouched = touchedFields.has(field.field_key);
        const fieldError = isTouched ? validationErrors[field.field_key] : null;
        const isRequired = getEffectiveRequiredStatus(field);
        
        // Get dynamic label
        const fieldLabel = getFieldDynamicLabel(field);
        
        // Get custom placeholder
        const placeholder = getFieldPlaceholder(field);

        // Validate field helper
        const validateField = (field, value) => {
            const isRequired = getEffectiveRequiredStatus(field);
            if (isRequired && (!value || value === '' || (Array.isArray(value) && value.length === 0))) {
                return `${field.field_label} is required`;
            }
            if (field.field_type === 'email' && value) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                    return 'Please enter a valid email address';
                }
            }
            if (field.field_type === 'phone' && value) {
                const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
                if (!phoneRegex.test(value.replace(/\s/g, ''))) {
                    return 'Please enter a valid phone number';
                }
            }
            return false;
        };

        const commonProps = {
            value: fieldValue || '',
            onChange: (e) => handleChange(field.field_key, e.target.value),
            onBlur: () => {
                // Mark field as touched on blur
                markFieldTouched(field.field_key);
                const error = validateField(field, fieldValue);
                setValidationErrors(prev => ({
                    ...prev,
                    [field.field_key]: error
                }));
            },
            isInvalid: !!fieldError,
            bg: isReadOnly ? "gray.50" : "white",
            isReadOnly: isReadOnly,
            isDisabled: isReadOnly,
            size: {base: "lg", md: "md"},
            height: {base: "48px", md: "40px"},
            placeholder: placeholder
        };

        switch (field.field_type) {
            case 'text':
            case 'email':
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <Input
                            {...commonProps}
                            type={field.field_type === 'email' ? 'email' : 'text'}
                        />
                        {fieldError && (
                            <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                {fieldError}
                            </Text>
                        )}
                    </FormControl>
                );

            case 'phone':
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <PhoneInput
                            international
                            defaultCountry="SG"
                            disabled={isReadOnly}
                            value={fieldValue || ''}
                            onChange={(value) => handleChange(field.field_key, value || '')}
                            inputComponent={Input}
                            inputProps={{
                                ...commonProps,
                                isInvalid: !!fieldError
                            }}
                        />
                        {fieldError && (
                            <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                {fieldError}
                            </Text>
                        )}
                    </FormControl>
                );

            case 'textarea':
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <Textarea
                            {...commonProps}
                            rows={3}
                        />
                        {fieldError && (
                            <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                {fieldError}
                            </Text>
                        )}
                    </FormControl>
                );

            case 'date':
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <Input
                            {...commonProps}
                            type="date"
                        />
                        {fieldError && (
                            <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                {fieldError}
                            </Text>
                        )}
                    </FormControl>
                );

            case 'number':
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <Input
                            {...commonProps}
                            type="number"
                            onChange={(e) => handleChange(field.field_key, parseFloat(e.target.value) || 0)}
                        />
                        {fieldError && (
                            <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                {fieldError}
                            </Text>
                        )}
                    </FormControl>
                );

            case 'select':
                const options = field.field_options || [];
                const isOtherOptionSelected = fieldValue && !options.includes(fieldValue) && fieldValue !== '';

                // For small number of options, show as buttons for better UX on both web and mobile
                if (options.length > 0 && options.length < 5) {
                    return (
                        <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                            <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                                {fieldLabel}
                                {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                            </FormLabel>
                            <VStack align="stretch" spacing={2}>
                                <SimpleGrid 
                                    columns={{ 
                                        base: 1, 
                                        sm: options.length === 2 ? 2 : (options.length === 3 ? 3 : 2), 
                                        md: Math.min(options.length, 4) 
                                    }} 
                                    spacing={2}
                                >
                                    {options.map((option) => {
                                        const isOthers = option === 'Others' || option === 'Other';
                                        const isSelected = isOthers ? isOtherOptionSelected : fieldValue === option;
                                        
                                        return (
                                            <Button
                                                key={option}
                                                onClick={() => {
                                                    if (isOthers) {
                                                        handleChange(field.field_key, '');
                                                    } else {
                                                        handleChange(field.field_key, option);
                                                    }
                                                }}
                                                colorScheme={isSelected ? "blue" : "gray"}
                                                variant={isSelected ? "solid" : "outline"}
                                                isDisabled={isReadOnly}
                                                size="md"
                                                py={{ base: 4, md: 5 }}
                                                px={4}
                                                whiteSpace="normal"
                                                height="auto"
                                                textAlign="center"
                                                fontWeight={isSelected ? "bold" : "medium"}
                                                borderColor={isSelected ? "blue.500" : "gray.300"}
                                                _hover={!isReadOnly ? {
                                                    bg: isSelected ? "blue.600" : "gray.100",
                                                    borderColor: "blue.500"
                                                } : {}}
                                            >
                                                {option}
                                            </Button>
                                        );
                                    })}
                                </SimpleGrid>
                                {isOtherOptionSelected && !isReadOnly && (
                                    <Input
                                        placeholder={placeholder || `Please specify ${fieldLabel.toLowerCase()}...`}
                                        value={fieldValue === 'Others' || fieldValue === 'Other' ? '' : fieldValue}
                                        onChange={(e) => handleChange(field.field_key, e.target.value)}
                                        size="md"
                                        mt={1}
                                        autoFocus
                                        borderColor="blue.300"
                                        focusBorderColor="blue.500"
                                    />
                                )}
                            </VStack>
                            {fieldError && (
                                <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                    {fieldError}
                                </Text>
                            )}
                        </FormControl>
                    );
                }

                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <Select
                            {...commonProps}
                            placeholder={`Select ${fieldLabel}`}
                        >
                            {(field.field_options || []).map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </Select>
                        {fieldError && (
                            <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                {fieldError}
                            </Text>
                        )}
                    </FormControl>
                );

            case 'boolean':
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}}>
                        <HStack>
                            <Checkbox
                                isChecked={!!fieldValue}
                                onChange={(e) => handleChange(field.field_key, e.target.checked)}
                                isDisabled={isReadOnly}
                            >
                                <FormLabel mb={0} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                                    {fieldLabel}
                                    {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                                </FormLabel>
                            </Checkbox>
                        </HStack>
                    </FormControl>
                );

            case 'file':
            case 'file_multiple':
                const selectedFilesForField = selectedFiles[field.field_key];
                const isUploading = uploadingFiles.has(field.field_key);
                const hasSelectedFiles = selectedFilesForField && selectedFilesForField.length > 0;
                const hasUploadedFiles = fieldValue && (Array.isArray(fieldValue) ? fieldValue.length > 0 : fieldValue !== '');
                
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <Box>
                            <input
                                type="file"
                                multiple={field.field_type === 'file_multiple'}
                                onChange={(e) => handleFileSelection(field.field_key, e.target.files)}
                                style={{ display: 'none' }}
                                id={`compulsory-file-input-${field.field_key}`}
                                accept="image/*,.pdf,.doc,.docx,.txt"
                                disabled={isReadOnly}
                            />
                            
                            {!isReadOnly && (
                                <Box textAlign="left" mb={hasUploadedFiles ? 4 : 0}>
                                    <Button
                                        onClick={() => {
                                            const input = document.getElementById(`compulsory-file-input-${field.field_key}`);
                                            if (input) input.click();
                                        }}
                                        disabled={isUploading}
                                        colorScheme="blue"
                                        variant={hasUploadedFiles ? "solid" : "outline"}
                                        size="sm"
                                        leftIcon={<Text fontSize="lg">+</Text>}
                                    >
                                        {hasUploadedFiles ? 
                                            (field.field_type === 'file_multiple' ? 'Add More' : 'Change File') 
                                            : 'Select File'}
                                    </Button>
                                </Box>
                            )}
                            
                            {selectedFilesForField && selectedFilesForField.length > 0 && (
                                <Box mt={4} p={3} border="1px dashed" borderColor="blue.300" borderRadius="md" bg="blue.50">
                                    <Text fontSize="xs" fontWeight="bold" mb={2} color="blue.700" textTransform="uppercase">
                                        Staged for upload:
                                    </Text>
                                    <VStack spacing={2} align="stretch">
                                        {Array.from(selectedFilesForField).map((file, index) => (
                                            <HStack key={index} justify="space-between" bg="white" p={2} borderRadius="sm" boxShadow="xs">
                                                <Text fontSize="xs" isTruncated flex={1}>
                                                    📄 {file.name}
                                                </Text>
                                                <IconButton
                                                    size="xs"
                                                    icon={<Text fontWeight="bold">×</Text>}
                                                    colorScheme="red"
                                                    variant="ghost"
                                                    onClick={() => {
                                                        const newFiles = Array.from(selectedFilesForField).filter((_, i) => i !== index);
                                                        const dataTransfer = new DataTransfer();
                                                        newFiles.forEach(f => dataTransfer.items.add(f));
                                                        setSelectedFiles(prev => ({ ...prev, [field.field_key]: dataTransfer.files }));
                                                    }}
                                                />
                                            </HStack>
                                        ))}
                                    </VStack>
                                    <Text fontSize="10px" color="blue.600" mt={2} fontStyle="italic">
                                        Files will be uploaded automatically when you save.
                                    </Text>
                                </Box>
                            )}
                            
                            {fieldValue && (
                                <Box mt={4}>
                                    <Text fontSize="xs" fontWeight="bold" mb={2} color="green.700" textTransform="uppercase">
                                        ✓ Uploaded
                                    </Text>
                                    <VStack spacing={2} align="stretch">
                                        {Array.isArray(fieldValue) ? (
                                            fieldValue.map((filePath, index) => (
                                                <HStack key={index} justify="space-between" bg="green.50" p={2} borderRadius="sm" border="1px solid" borderColor="green.200">
                                                    <Text fontSize="xs" color="green.700" isTruncated flex={1}>
                                                        📎 {filePath.split('/').pop()}
                                                    </Text>
                                                    {!isReadOnly && (
                                                        <IconButton
                                                            size="xs"
                                                            icon={<Text fontWeight="bold">×</Text>}
                                                            colorScheme="red"
                                                            variant="ghost"
                                                            onClick={() => {
                                                                const newValue = fieldValue.filter((_, i) => i !== index);
                                                                handleChange(field.field_key, newValue);
                                                            }}
                                                        />
                                                    )}
                                                </HStack>
                                            ))
                                        ) : (
                                            <HStack justify="space-between" bg="green.50" p={2} borderRadius="sm" border="1px solid" borderColor="green.200">
                                                <Text fontSize="xs" color="green.700" isTruncated flex={1}>
                                                    📎 {fieldValue.split('/').pop()}
                                                </Text>
                                                {!isReadOnly && (
                                                    <IconButton
                                                        size="xs"
                                                        icon={<Text fontWeight="bold">×</Text>}
                                                        colorScheme="red"
                                                        variant="ghost"
                                                        onClick={() => handleChange(field.field_key, '')}
                                                    />
                                                )}
                                            </HStack>
                                        )}
                                    </VStack>
                                </Box>
                            )}
                        </Box>
                    </FormControl>
                );

            default:
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <Input
                            {...commonProps}
                            type="text"
                        />
                        {fieldError && (
                            <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                {fieldError}
                            </Text>
                        )}
                    </FormControl>
                );
        }
    };

    // Helper function to get field value from formData
    const getFieldValue = (formData, fieldKey) => {
        // First check if it's a direct key
        if (formData[fieldKey] !== undefined && formData[fieldKey] !== null && formData[fieldKey] !== '') {
            return formData[fieldKey];
        }
        
        // Then check for nested keys (e.g., "personal_details.gender")
        if (fieldKey.includes('.')) {
            const keys = fieldKey.split('.');
            let current = formData;
            for (const key of keys) {
                if (current && typeof current === 'object' && key in current) {
                    current = current[key];
                } else {
                    return '';
                }
            }
            return current || '';
        }
        
        return '';
    };

    // Helper function to check if a field is conditionally required
    const isFieldConditionallyRequired = (field) => {
        if (!field.conditional_requirements) {
            return false;
        }

        const condition = field.conditional_requirements.required_when;
        if (!condition) {
            return false;
        }

        const triggerField = condition.field;
        const triggerValue = getFieldValue(formData, triggerField);
        const operator = condition.operator;
        const expectedValue = condition.value;

        switch (operator) {
            case 'equals':
                return valuesEqualCountryAware(triggerValue, expectedValue);
            case 'contains':
                return (
                    Array.isArray(triggerValue) &&
                    triggerValue.some((tv) => valuesEqualCountryAware(tv, expectedValue))
                );
            case 'in':
                return (
                    Array.isArray(expectedValue) &&
                    expectedValue.some((ev) => valuesEqualCountryAware(triggerValue, ev))
                );
            case 'not_equals':
                return !valuesEqualCountryAware(triggerValue, expectedValue);
            default:
                return false;
        }
    };

    // Get effective requirement status (static or conditional)
    const getEffectiveRequiredStatus = (field) => {
        if (field.conditional_requirements) {
            return isFieldConditionallyRequired(field);
        }
        return field.is_required;
    };

    // Check if required additional fields are filled
    const checkRequiredAdditionalFields = () => {
        const requiredFields = additionalFields.filter(field => {
            if (templateFieldIds !== null && !templateFieldIds.includes(field.id)) return false;
            const isRequired = getEffectiveRequiredStatus(field);
            return isRequired &&
                field.field_key !== 'legal.declaration_date' &&
                !field.field_key.includes('signature');
        });
        
        for (const field of requiredFields) {
            const fieldValue = getFieldValue(formData, field.field_key);
            const stagedFiles = selectedFiles[field.field_key];
            
            // A field is valid if it has a value in DB/formData OR it has staged files ready to be uploaded
            const hasValue = (fieldValue && fieldValue !== '' && (!Array.isArray(fieldValue) || fieldValue.length > 0));
            const hasStagedFiles = (stagedFiles && stagedFiles.length > 0);
            
            if (!hasValue && !hasStagedFiles) {
                return false;
            }
        }
        return true;
    };

    // Helper functions for progress calculation and visibility
    const shouldShowField = (field) => {
        if (templateFieldIds !== null && !templateFieldIds.includes(field.id)) return false;
        if (!field.conditional_requirements || !field.conditional_requirements.show_when) return true;
        const { field: triggerField, value: triggerValue, operator } = field.conditional_requirements.show_when;
        const currentVal = getFieldValue(formData, triggerField);
        if (operator === 'equals') return valuesEqualCountryAware(currentVal, triggerValue);
        if (operator === 'not_equals') return !valuesEqualCountryAware(currentVal, triggerValue);
        if (operator === 'contains') return Array.isArray(currentVal) && currentVal.includes(triggerValue);
        return true;
    };

    // Derived: does this cert require email verification per its policy?
    const needsEmailVerification = requiresEmailVerification(policyData);

    // Derived: does this cert require payment? False when commitment_type is 'interest_registration' or 'none'.
    const needsPayment = !policyData || !['interest_registration', 'none'].includes(policyData.commitment_type);

    // Calculate compulsory fields completion for progress bar
    // Includes basic fields + all required additional fields
    const getCompulsoryFieldsProgress = () => {
        const compulsoryFields = new Map();
        
        // 1. Course selection as first compulsory item
        compulsoryFields.set('course', !!formData.draft_cert_run_code);
        
        // 2. Basic required fields (core system fields) — only those in the active template
        const basicRequiredFieldKeys = ['nationality', 'dob', 'participant_name', 'participant_email', 'mobile_no'];
        basicRequiredFieldKeys.forEach(key => {
            if (!fieldInTemplate(key)) return;
            const val = key === 'dob' ? formData.dob : formData[key];
            compulsoryFields.set(key, !!(val && val.toString().trim() !== ''));
        });

        if (fieldInTemplate('nric')) {
            const nricApplies = isSingaporeCitizenOrPR(formData.nationality) ||
                requiresPassport(formData.nationality) ||
                requiresFIN(formData.nationality);
            if (nricApplies || !formData.nationality) {
                compulsoryFields.set('nric', !!(formData.nric && !validationErrors.nric));
            }
        }
        
        // 3. Add all required additional fields from database
        // Use Map to ensure each field_key is only counted once
        if (additionalFields && additionalFields.length > 0) {
            additionalFields.forEach(field => {
                // Skip if already in basicRequiredFieldKeys to avoid double counting
                if (basicRequiredFieldKeys.includes(field.field_key)) return;
                
                // Exclude auto-filled and signature fields from progress count
                if (field.field_key === 'legal.declaration_date' || 
                    field.field_key.includes('signature') || 
                    field.field_key === 'terms') return;

                const isRequired = getEffectiveRequiredStatus(field);
                const isVisible = shouldShowField(field);
                
                if (isRequired && isVisible) {
                    const fieldValue = getFieldValue(formData, field.field_key);
                    const stagedFiles = selectedFiles[field.field_key];
                    const isFilled = (
                        (fieldValue && fieldValue !== '' && (!Array.isArray(fieldValue) || fieldValue.length > 0)) ||
                        (stagedFiles && stagedFiles.length > 0)
                    );
                    compulsoryFields.set(field.field_key, !!isFilled);
                }
            });
        }
        
        const allBaseFilled = Array.from(compulsoryFields.values()).every(v => v);
        
        const totalFilledCount = Array.from(compulsoryFields.values()).filter(v => v).length;
        const totalCount = compulsoryFields.size;
        
        return { 
            filledCount: totalFilledCount, 
            totalCount, 
            canSubmit: allBaseFilled,
            allFilled: allBaseFilled
        };
    };
    
    const progressData = getCompulsoryFieldsProgress();

    // Send verification email
    const sendVerificationEmail = async (registrationId, participantEmail, participantName) => {
        if (!registrationId || !participantEmail) return false;

        setIsSendingVerification(true);
        try {
            const { data, error } = await supabase.functions.invoke('send-verification-email', {
                body: {
                    registrationId,
                    participantEmail,
                    participantName: participantName || formData.participant_name || 'Participant',
                    baseUrl: getAppOrigin(),
                },
            });

            if (error) throw error;

            if (data.success) {
                setEmailVerificationSent(true);
                setEmailVerified(false); // Reset to false when new email is sent
                return true; // Return success
            } else {
                throw new Error(data.error || 'Failed to send verification email');
            }
        } catch (error) {
            console.error('Error sending verification email:', error);
            // Only show error toast if not called from initial save (to avoid multiple toasts)
            // Error will be handled by caller
            return false;
        } finally {
            setIsSendingVerification(false);
        }
    };

    // File upload handlers for compulsory fields
    const handleFileSelection = (fieldKey, files) => {
        if (!files || files.length === 0) return;
        setSelectedFiles(prev => ({
            ...prev,
            [fieldKey]: files
        }));
    };

    const handleCompulsoryFileUpload = async (field) => {
        const files = selectedFiles[field.field_key];
        if (!files || files.length === 0) {
            toast({
                title: "No files selected",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Auto-save if needed
        if (!registrationId && formData.participant_name && formData.participant_email && formData.draft_cert_run_code) {
            try {
                const registrationData = {
                    participant_name: formData.participant_name,
                    participant_email: formData.participant_email,
                    nationality: formData.nationality || '',
                    nric: formData.nric || '',
                    dob: formData.dob || '',
                    birth_age: formData.birth_age || '',
                    mobile_no: formData.mobile_no || '',
                    registration_stage: "draft",
                    draft_cert_run_code: formData.draft_cert_run_code,
                };
                
                const { data, error } = await supabase
                    .from('registration')
                    .insert([registrationData])
                    .select("id")
                    .single();
                
                if (error) throw error;
                
                const savedId = data.id;
                setRegistrationId(savedId);
                
                // Send verification email only if required by policy
                if (formData.participant_email && needsEmailVerification) {
                    await sendVerificationEmail(savedId, formData.participant_email, formData.participant_name);
                }
            } catch (error) {
                console.error('Auto-save error:', error);
                toast({
                    title: "Registration Required",
                    description: "Please fill in your basic information (name, email, etc.) first to enable file uploads.",
                    status: "warning",
                    duration: 5000,
                    isClosable: true,
                });
                return;
            }
        } else if (!registrationId) {
            toast({
                title: "Registration Required",
                description: "Please fill in your basic information (name, email, etc.) first to enable file uploads.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        if (uploadingFiles.has(field.field_key)) {
            toast({
                title: "Please wait for the current upload to complete.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            setUploadingFiles(prev => new Set(prev).add(field.field_key));
            
            const studentName = formData.participant_name || 'Unknown';
            const sanitizedName = studentName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
            
            const urlParams = new URLSearchParams(window.location.search);
            const isFullCertification = (formData.full_certification_id || urlParams.get('full_certification')) ? true : false;
            const fullCertificationId = formData.full_certification_id || urlParams.get('full_certification');
            const certificationRunId = formData.certification_run_id || urlParams.get('certification_run_id');
            
            const uploadedFiles = [];
            const existingValue = getFieldValue(formData, field.field_key);
            const existingFilesCount = Array.isArray(existingValue) ? existingValue.length : (existingValue ? 1 : 0);
            
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const fileExtension = file.name.split('.').pop() || '';
                const timestamp = new Date().toISOString().split('T')[0];
                const fieldIdentifier = field.field_key.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
                const fileName = `${sanitizedName}_${fieldIdentifier}_${timestamp}_${existingFilesCount + i + 1}.${fileExtension}`;
                
                let folderPath;
                if (isFullCertification) {
                    if (!fullCertificationId || !certificationRunId) {
                        toast({
                            title: "Select certification run",
                            description: "Please select a certification run before uploading files.",
                            status: "warning",
                            duration: 4000,
                            isClosable: true,
                        });
                        break;
                    }
                    folderPath = `registrations/full__cert/${fullCertificationId}/${certificationRunId}/${sanitizedName}`;
                } else if (registrationId) {
                    folderPath = `registrations/${registrationId}`;
                } else {
                    toast({
                        title: "Registration Required",
                        description: "Please fill in your basic information first to enable file uploads.",
                        status: "warning",
                        duration: 5000,
                        isClosable: true,
                    });
                    break;
                }
                
                const filePath = `${folderPath}/${fileName}`;
                
                const { error } = await supabase.storage
                    .from('Registration_data')
                    .upload(filePath, file, {
                        cacheControl: '3600',
                        upsert: true
                    });

                if (error) {
                    console.error('Upload error:', error);
                    toast({
                        title: "Upload Failed",
                        description: `Failed to upload ${file.name}: ${error.message}`,
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                    });
                    continue;
                }

                uploadedFiles.push(filePath);
            }

            if (uploadedFiles.length > 0) {
                const currentValue = getFieldValue(formData, field.field_key);
                const newValue = field.field_type === 'file_multiple' 
                    ? [...(Array.isArray(currentValue) ? currentValue : currentValue ? [currentValue] : []), ...uploadedFiles]
                    : uploadedFiles[0];
                
                handleChange(field.field_key, newValue);
                
                setSelectedFiles(prev => {
                    const newState = { ...prev };
                    delete newState[field.field_key];
                    return newState;
                });
                
                toast({
                    title: "Upload Successful",
                    description: `Successfully uploaded ${uploadedFiles.length} file(s)`,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('File upload error:', error);
            toast({
                title: "Upload Failed",
                description: error.message || "An error occurred during upload",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setUploadingFiles(prev => {
                const newSet = new Set(prev);
                newSet.delete(field.field_key);
                return newSet;
            });
        }
    };

    // Resend verification email
    const resendVerificationEmail = async () => {
        if (!registrationId || !formData.participant_email) {
            toast({
                title: "Cannot Resend",
                description: "Please save your registration first.",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Show toast for resend (different from initial send)
        const result = await sendVerificationEmail(
            registrationId,
            formData.participant_email,
            formData.participant_name
        );
        
        // Show toast after resend
        if (result) {
            toast({
                title: "Verification Email Resent",
                description: `A new verification email has been sent to ${formData.participant_email}. Please check your inbox.`,
                status: "info",
                duration: 4000,
                isClosable: true,
            });
        }
    };

    // Fetch enrollment policy when a cert run is selected
    useEffect(() => {
        const certRunId = formData.draft_cert_run_code;
        if (!certRunId || !courseRuns.length) return;

        const selectedRun = courseRuns.find(r => r.id === certRunId);
        if (!selectedRun) return;

        const certCode = selectedRun.full_certification?.code || selectedRun.full_cert_type_code;
        getEffectiveCertPolicy(supabase, certCode, certRunId).then(policy => {
            setPolicyData(policy);
        });
    }, [formData.draft_cert_run_code, courseRuns]);

    // Check verification status periodically (only if policy requires it)
    useEffect(() => {
        if (!registrationId || emailVerified || !needsEmailVerification) return;

        const checkVerificationStatus = async () => {
            try {
                const { data, error } = await fetchPublicRegistration(supabase, registrationId);

                if (!error && data && data.email_verified) {
                    setEmailVerified(true);
                    toast({
                        title: "Email Verified!",
                        description: "Your email has been verified. You can now submit your registration.",
                        status: "success",
                        duration: 3000,
                        isClosable: true,
                    });
                }
            } catch (error) {
                console.error('Error checking verification status:', error);
            }
        };

        // Check every 5 seconds if not verified
        const interval = setInterval(checkVerificationStatus, 5000);
        return () => clearInterval(interval);
    }, [registrationId, emailVerified, needsEmailVerification]);

    // Helper to upload all staged files automatically and return the new paths
    const uploadAllStagedFiles = async (targetId) => {
        const fieldKeys = Object.keys(selectedFiles);
        if (fieldKeys.length === 0) return {};

        const newPaths = {};
        
        for (const fieldKey of fieldKeys) {
            const files = selectedFiles[fieldKey];
            if (!files || files.length === 0) continue;

            const field = additionalFields.find(f => f.field_key === fieldKey) || 
                          compulsoryCustomFields.find(f => f.field_key === fieldKey);
            
            if (field) {
                try {
                    // Temporarily set uploading state for this field
                    setUploadingFiles(prev => new Set(prev).add(fieldKey));
                    
                    const studentName = formData.participant_name || 'Unknown';
                    const sanitizedName = studentName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
                    
                    const urlParams = new URLSearchParams(window.location.search);
                    const isFullCert = (formData.full_certification_id || urlParams.get('full_certification')) ? true : false;
                    const fCertId = formData.full_certification_id || urlParams.get('full_certification');
                    const cRunId = formData.certification_run_id || urlParams.get('certification_run_id');
                    
                    const uploadedFiles = [];
                    const existingValue = getFieldValue(formData, fieldKey);
                    const existingFilesCount = Array.isArray(existingValue) ? existingValue.length : (existingValue ? 1 : 0);
                    
                    for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        const fileExtension = file.name.split('.').pop() || '';
                        const timestamp = new Date().toISOString().split('T')[0];
                        const fieldIdentifier = fieldKey.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
                        const fileName = `${sanitizedName}_${fieldIdentifier}_${timestamp}_${existingFilesCount + i + 1}.${fileExtension}`;
                        
                        let folderPath;
                        if (isFullCert && fCertId && cRunId) {
                            folderPath = `registrations/full__cert/${fCertId}/${cRunId}/${sanitizedName}`;
                        } else {
                            folderPath = `registrations/${targetId}`;
                        }
                        
                        const filePath = `${folderPath}/${fileName}`;
                        const { error } = await supabase.storage
                            .from('Registration_data')
                            .upload(filePath, file, { cacheControl: '3600', upsert: true });

                        if (error) throw error;
                        uploadedFiles.push(filePath);
                    }

                    if (uploadedFiles.length > 0) {
                        const currentValue = getFieldValue(formData, fieldKey);
                        const newValue = field.field_type === 'file_multiple' 
                            ? [...(Array.isArray(currentValue) ? currentValue : currentValue ? [currentValue] : []), ...uploadedFiles]
                            : uploadedFiles[0];
                        
                        newPaths[fieldKey] = newValue;
                        
                        // Update local state with proper nesting
                        setFormData(prev => {
                            const newData = { ...prev };
                            if (fieldKey.includes('.')) {
                                const keys = fieldKey.split('.');
                                let current = newData;
                                for (let i = 0; i < keys.length - 1; i++) {
                                    if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
                                        current[keys[i]] = {};
                                    }
                                    current = current[keys[i]];
                                }
                                current[keys[keys.length - 1]] = newValue;
                            } else {
                                newData[fieldKey] = newValue;
                            }
                            return newData;
                        });

                        // Clear selected files for this field
                        setSelectedFiles(prev => {
                            const newState = { ...prev };
                            delete newState[fieldKey];
                            return newState;
                        });
                    }
                } catch (error) {
                    console.error(`Upload error for ${fieldKey}:`, error);
                    toast({
                        title: "Upload Failed",
                        description: `Failed to upload files for ${field.field_label}. Please try again manually.`,
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                    });
                } finally {
                    setUploadingFiles(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(fieldKey);
                        return newSet;
                    });
                }
            }
        }
        return newPaths;
    };

    // Helper to merge dotted keys into a nested object
    const mergeDottedPaths = (target, dottedPaths) => {
        const result = { ...target };
        Object.entries(dottedPaths).forEach(([key, value]) => {
            if (key.includes('.')) {
                const keys = key.split('.');
                let current = result;
                for (let i = 0; i < keys.length - 1; i++) {
                    if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
                        current[keys[i]] = {};
                    }
                    current = current[keys[i]];
                }
                current[keys[keys.length - 1]] = value;
            } else {
                result[key] = value;
            }
        });
        return result;
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            devLog('Starting registration process...');
            devLog('Form data:', formData);

            if (!formData.draft_cert_run_code) {
                throw new Error('No certification run selected');
            }

            // Get the effective enrollment policy for this cert run
            const selectedCertRun = courseRuns.find(c => c.id === formData.draft_cert_run_code);
            const certCode = selectedCertRun?.full_certification?.code;
            const effectivePolicy = certCode
                ? await getEffectiveCertPolicy(supabase, certCode, formData.draft_cert_run_code)
                : policyData;

            // Email verification gate (like Registration.js)
            if (effectivePolicy?.needs_email_verification && !emailVerified) {
                // Create a draft registration if one doesn't exist yet
                let currentRegId = registrationId;
                if (!currentRegId) {
                    const draftData = {
                        participant_name: formData.participant_name,
                        participant_email: formData.participant_email,
                        nationality: formData.nationality,
                        nric: formData.nric,
                        dob: formData.dob,
                        birth_age: formData.birth_age,
                        mobile_no: formData.mobile_no,
                        registration_stage: "draft",
                        draft_cert_run_code: formData.draft_cert_run_code,
                    };

                    const { data: draftResult, error: draftError } = await supabase
                        .from('registration')
                        .insert([draftData])
                        .select("id")
                        .single();

                    if (draftError) throw draftError;
                    currentRegId = draftResult.id;
                    setRegistrationId(currentRegId);

                    window.history.replaceState(
                        { preventScroll: true },
                        '',
                        `/register/${currentRegId}`
                    );
                }

                // Upload any staged files
                await uploadAllStagedFiles(currentRegId);

                // Send verification email
                await sendVerificationEmail(
                    currentRegId,
                    formData.participant_email,
                    formData.participant_name
                );

                setIsSubmitting(false);
                toast({
                    title: "Email Verification Required",
                    description: "A verification email has been sent. Please verify your email, then submit again.",
                    status: "info",
                    duration: 5000,
                    isClosable: true,
                });
                return;
            }

            // Upload staged files
            let currentRegId = registrationId;
            let newFilePaths = {};
            if (currentRegId) {
                newFilePaths = await uploadAllStagedFiles(currentRegId);
            }

            // Fetch current registration to check for rejection_reason and preserve rejection_history
            let existingAdditionalData = {};
            let hasRejectionReason = false;

            if (currentRegId) {
                const { data: currentRegistration, error: fetchError } = await supabase
                    .from('registration')
                    .select('rejection_reason, additional_data')
                    .eq('id', currentRegId)
                    .single();

                if (fetchError) {
                    console.error('Error fetching current registration:', fetchError);
                }

                hasRejectionReason = currentRegistration?.rejection_reason && 
                                      currentRegistration.rejection_reason.trim() !== '';
                existingAdditionalData = currentRegistration?.additional_data || {};
            }

            // Prepare additional data from form fields with proper nesting
            let additionalData = { ...existingAdditionalData };
            additionalData = mergeDottedPaths(additionalData, newFilePaths);

            devLog('Fetching additional fields...');
            const { data: additionalFieldsList, error: fieldsError } = await supabase
                .from('additional_registration_fields')
                .select('field_key')
                .or('field_status.is.null,field_status.eq.active')
                .eq('is_original_field', false);

            if (fieldsError) {
                console.error('Error fetching additional fields:', fieldsError);
                throw new Error(`Failed to fetch additional fields: ${fieldsError.message}`);
            }

            if (additionalFieldsList) {
                additionalFieldsList.forEach(field => {
                    const fieldValue = getFieldValue(formData, field.field_key);
                    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
                        const keys = field.field_key.split('.');
                        if (keys.length > 1) {
                            let current = additionalData;
                            for (let i = 0; i < keys.length - 1; i++) {
                                if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
                                    current[keys[i]] = {};
                                }
                                current = current[keys[i]];
                            }
                            current[keys[keys.length - 1]] = fieldValue;
                        } else {
                            additionalData[field.field_key] = fieldValue;
                        }
                    }
                });
            }

            if (existingAdditionalData.rejection_history) {
                additionalData.rejection_history = existingAdditionalData.rejection_history;
            }

            const today = new Date().toISOString().split('T')[0];
            if (!additionalData.legal) {
                additionalData.legal = {};
            }
            additionalData.legal.declaration_date = today;

            devLog('Additional data prepared:', additionalData);

            // Determine registration stage using the enrollment policy (same as Registration.js)
            const isRunConfirmed = !!selectedCertRun?.is_confirmed;
            const registrationStage = hasRejectionReason
                ? 'amended'
                : getNextRegistrationStage(effectivePolicy, isRunConfirmed);

            devLog('Resolved registration stage:', registrationStage);

            const registrationPayload = {
                participant_name: formData.participant_name,
                participant_email: formData.participant_email,
                nationality: formData.nationality,
                nric: formData.nric,
                dob: formData.dob,
                birth_age: formData.birth_age,
                mobile_no: formData.mobile_no,
                registration_stage: registrationStage,
                draft_cert_run_code: formData.draft_cert_run_code,
                additional_data: additionalData,
                rejection_reason: null,
                policy_snapshot: effectivePolicy,
                approval_required: effectivePolicy?.needs_approval || false,
                approval_status: effectivePolicy?.needs_approval ? 'pending' : null,
                payment_balance: !effectivePolicy || !['interest_registration', 'none'].includes(effectivePolicy.commitment_type) ? undefined : 0,
            };

            let savedRegId;
            if (currentRegId) {
                devLog('Updating registration with data:', registrationPayload);
                const { data, error } = await supabase
                    .from('registration')
                    .update(registrationPayload)
                    .eq('id', currentRegId)
                    .select("id,participant_email")
                    .single();

                if (error) throw error;
                savedRegId = data.id;
            } else {
                devLog('Creating new registration with data:', registrationPayload);
                // Also upload files after creating
                const { data, error } = await supabase
                    .from('registration')
                    .insert([registrationPayload])
                    .select("id,participant_email")
                    .single();

                if (error) throw error;
                savedRegId = data.id;
                setRegistrationId(savedRegId);

                // Upload any staged files now that we have an ID
                newFilePaths = await uploadAllStagedFiles(savedRegId);
                if (Object.keys(newFilePaths).length > 0) {
                    const finalAdditionalData = mergeDottedPaths(registrationPayload.additional_data || {}, newFilePaths);
                    await supabase
                        .from('registration')
                        .update({ additional_data: finalAdditionalData })
                        .eq('id', savedRegId);
                }
            }

            // Create registration fee invoice via RPC (anon users cannot insert into invoices directly)
            const ct = effectivePolicy?.commitment_type || 'full';
            if (ct === 'registration_fee' && registrationStage === 'registration_fee_pending') {
                const feeAmount = Number(effectivePolicy?.registration_fee_amount || 0);
                if (feeAmount > 0) {
                    const { error: invoiceError } = await supabase.rpc('ensure_registration_fee_invoice', {
                        p_registration_id: savedRegId,
                    });
                    if (invoiceError) {
                        console.error('ensure_registration_fee_invoice error:', invoiceError);
                    }
                }
            }

            // Registration received email — fire-and-forget; do not block navigation
            const isAmendment = hasRejectionReason;
            if (
                isRunConfirmed &&
                !isAmendment &&
                (!effectivePolicy?.needs_approval ||
                    effectivePolicy?.commitment_type === 'registration_fee')
            ) {
                queueRegistrationReceivedEmail(savedRegId);
            }

            setIsSubmitting(false);

            const successMessage = hasRejectionReason 
                ? "Your registration has been resubmitted as 'Amended' and will be reviewed again."
                : "Your registration has been submitted successfully!";

            toast({
                title: hasRejectionReason ? "Registration Resubmitted" : "Registration Successful",
                description: successMessage,
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            clearLocalDraft();

            // Navigate immediately — RegistrationHub handles post-submit stages
            navigate(`/register/${savedRegId}`, { state: { refresh: Date.now() } });
        } catch (error) {
            console.error('Unexpected error during registration:', error);
            setIsSubmitting(false);
            toast({
                title: "Registration Failed",
                description: `Registration failed due to an unexpected error: ${error.message}. Please try again.`,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    // Get certification info
    const params = new URLSearchParams(location.search);
    const fullCertification = params.get('full_certification');
    const selectedCertRun = courseRuns.find(course => course.id === formData.draft_cert_run_code);
    const selectedCourseRun = selectedCertRun?.full_cert_many_course_run;
    const selectedCertType = selectedCertRun?.full_certification;
    console.log(selectedCourseRun, "selectedCourseRun")

    // Determine if form should be read-only (only editable when stage is "draft" or "rejected")
    const isReadOnly = !!formData.registration_stage &&
                       formData.registration_stage !== "draft" &&
                       formData.registration_stage !== "rejected";

    // Lock full course and intake selection once participant is past draft (e.g. pending, approved, etc.)
    const isCourseAndIntakeLocked = formData.registration_stage != null && formData.registration_stage !== "draft";

    return (
        <div>
            {/* Help Popup - shows after 5 minutes of inactivity */}
            <HelpPopup pageName="Course Registration" />

            {/* Floating Progress Bar - Always at top of viewport */}
            {!loadingCourseRuns && !loadingRegistration && !isReadOnly && formData.draft_cert_run_code && (
                <FloatingProgressBar 
                    filledCount={progressData.filledCount}
                    totalCount={progressData.totalCount}
                    emailVerified={emailVerified}
                    emailVerificationSent={emailVerificationSent}
                    participantEmail={formData.participant_email}
                    onResendVerification={resendVerificationEmail}
                    isSendingVerification={isSendingVerification}
                    showEmailVerification={registrationId && needsEmailVerification}
                />
            )}

            {(loadingCourseRuns || loadingRegistration) ? (
                <Center h="60vh">
                    <VStack spacing={4}>
                        <Spinner size="xl" thickness="4px" speed="0.65s" color="brand.600"/>
                        <Text color="gray.600">
                            {loadingRegistration ? "Loading registration..." : "Loading courses..."}
                        </Text>
                    </VStack>
                </Center>
            ) : (
                <Stack
                    spacing={{base: 4, md: 5}}
                    width="100%"
                    bg={"white"}
                    px={{base: 4, md: 8, lg: 10}}
                    pt={{base: formData.draft_cert_run_code && !isReadOnly ? "80px" : 5, md: formData.draft_cert_run_code && !isReadOnly ? "90px" : 8}} // Account for floating progress bar
                    pb={{base: "120px", md: "100px"}} // Extra bottom padding for fixed action button
                    borderRadius={{base: "lg", md: "xl"}}
                    boxShadow={{base: "sm", md: "lg"}}
                    mx={0}
                >
                    {/* Registration Status Banner */}
                    {isReadOnly && formData.registration_stage && (
                        <Alert 
                            status={
                                formData.registration_stage === "approved" ? "success" :
                                formData.registration_stage === "amended" ? "info" :
                                "warning"
                            } 
                            mb={6} 
                            borderRadius="md"
                            variant="left-accent"
                        >
                            <AlertIcon />
                            <Box flex="1">
                                <Text fontSize={{base: "xs", md: "sm"}} fontWeight="bold" mb={1}>
                                    Registration Status: {formData.registration_stage.charAt(0).toUpperCase() + formData.registration_stage.slice(1)}
                                </Text>
                                <Text fontSize={{base: "10px", md: "xs"}}>
                                    {formData.registration_stage === "approved" 
                                        ? "Your registration has been approved. This form is now read-only."
                                        : formData.registration_stage === "amended"
                                        ? "Your registration has been resubmitted and is under review. This form is read-only."
                                        : "This registration is no longer editable."}
                                </Text>
                            </Box>
                        </Alert>
                    )}

                    {formData.rejection_reason && (
                        <Alert status="error" mb={4} borderRadius="md" bg="red.50" borderColor="red.200" borderWidth="1px">
                            <AlertIcon color="red.500" />
                            <Box>
                                <Text fontSize={{base: "xs", md: "sm"}} fontWeight="bold" color="red.700">
                                    Your registration requires amendment
                                </Text>
                                <Text fontSize={{base: "10px", md: "xs"}} mt={1} color="red.600">
                                    <strong>Reason:</strong> {formData.rejection_reason}
                                </Text>
                                <Text fontSize={{base: "10px", md: "xs"}} mt={2} color="red.600">
                                    💡 Please address the issues above and resubmit. Your registration will be marked as "Amended" for review.
                                </Text>
                            </Box>
                        </Alert>
                    )}

                    <Box w={"100%"} borderRadius="md" p={{base: 4, md: 6}}>
                        {/* Course selection is shown below */}

                        {/* Certification Type Info - Show when a cert run is selected */}
                        {selectedCertType && formData.draft_cert_run_code && (() => {
                            // Use discount from cert run if available, otherwise fall back to cert type discount
                            const discount = selectedCertRun?.discount !== undefined && selectedCertRun?.discount !== null 
                                ? parseFloat(selectedCertRun.discount || 0)
                                : (selectedCertType.discount !== undefined && selectedCertType.discount !== null 
                                    ? parseFloat(selectedCertType.discount || 0)
                                    : 0);
                            const cost = selectedCertType.cost !== undefined && selectedCertType.cost !== null 
                                ? parseFloat(selectedCertType.cost || 0)
                                : 0;
                            
                            // GST calculation (from organization settings, applied to course fees only)
                            const subtotal = cost - discount;
                            const gstRate = orgGstSettings.gst_enabled ? (orgGstSettings.gst_rate / 100) : 0;
                            const gstAmount = subtotal * gstRate;
                            const totalPayable = subtotal + gstAmount;
                            const gstPercentDisplay = orgGstSettings.gst_enabled ? orgGstSettings.gst_rate : 0;
                            
                            return (
                                <Box 
                                    mb={5} 
                                    p={{base: 4, md: 5}} 
                                    bg="blue.50" 
                                    borderRadius="lg" 
                                    borderWidth="1px" 
                                    borderColor="blue.200"
                                    mt={-2}
                                >
                                    <VStack align="start" spacing={3}>
                                        {selectedCertType.description && (
                                            <Box>
                                                <Text fontSize={{base: "xs", md: "sm"}} fontWeight="semibold" color="gray.700" mb={2}>
                                                    Programme Description
                                                </Text>
                                                <Text fontSize={{base: "xs", md: "sm"}} color="gray.600" lineHeight="1.6">
                                                    {selectedCertType.description}
                                                </Text>
                                            </Box>
                                        )}
                                        {cost > 0 && (
                                            <Box 
                                                w="100%" 
                                                bg="white" 
                                                p={{base: 3, md: 4}} 
                                                borderRadius="md" 
                                                boxShadow="sm"
                                            >
                                                <VStack spacing={2} align="stretch">
                                                    {/* Programme Fee */}
                                                    <Flex justify="space-between" align="center">
                                                        <Text fontSize={{base: "xs", md: "sm"}} color="gray.600">
                                                            Programme Fee
                                                        </Text>
                                                        <Text fontSize={{base: "sm", md: "md"}} fontWeight="medium" color="gray.800">
                                                            S${cost.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </Text>
                                                    </Flex>
                                                    
                                                    {/* Discount (if applicable) */}
                                                    {discount > 0 && (
                                                        <Flex justify="space-between" align="center">
                                                            <Text fontSize={{base: "xs", md: "sm"}} color="green.600">
                                                                Discount
                                                            </Text>
                                                            <Text fontSize={{base: "sm", md: "md"}} fontWeight="medium" color="green.600">
                                                                -S${discount.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </Text>
                                                        </Flex>
                                                    )}
                                                    
                                                    {/* Subtotal (before GST) - only show if there's a discount */}
                                                    {discount > 0 && (
                                                        <Flex justify="space-between" align="center" pt={1} borderTop="1px dashed" borderColor="gray.200">
                                                            <Text fontSize={{base: "xs", md: "sm"}} color="gray.600">
                                                                Subtotal
                                                            </Text>
                                                            <Text fontSize={{base: "sm", md: "md"}} fontWeight="medium" color="gray.800">
                                                                S${subtotal.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </Text>
                                                        </Flex>
                                                    )}
                                                    
                                                    {/* GST - only show if enabled */}
                                                    {orgGstSettings.gst_enabled && gstAmount > 0 && (
                                                        <Flex justify="space-between" align="center">
                                                            <Text fontSize={{base: "xs", md: "sm"}} color="gray.600">
                                                                GST ({gstPercentDisplay}%)
                                                            </Text>
                                                            <Text fontSize={{base: "sm", md: "md"}} fontWeight="medium" color="gray.700">
                                                                S${gstAmount.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </Text>
                                                        </Flex>
                                                    )}
                                                    
                                                    {/* Total Payable */}
                                                    <Flex 
                                                        justify="space-between" 
                                                        align="center" 
                                                        pt={2} 
                                                        mt={1}
                                                        borderTop="2px solid" 
                                                        borderColor="brand.400"
                                                    >
                                                        <Text fontSize={{base: "sm", md: "md"}} fontWeight="bold" color="gray.800">
                                                            Total Payable
                                                            {orgGstSettings.gst_enabled && gstAmount > 0 && (
                                                                <Text as="span" fontSize="xs" fontWeight="normal" color="gray.500" ml={1}>
                                                                    (incl. GST)
                                                                </Text>
                                                            )}
                                                        </Text>
                                                        <Text fontSize={{base: "lg", md: "xl"}} fontWeight="bold" color="brand.600">
                                                            S${totalPayable.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </Text>
                                                    </Flex>
                                                </VStack>
                                            </Box>
                                        )}
                                    </VStack>
                                </Box>
                            );
                        })()}

                        {/* Certification Selection - Two Step Process */}
                        <VStack spacing={4} align="stretch" mb={5}>
                            {/* Step 1: Select Certification Name */}
                            <FormControl>
                                <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                                    Select Course
                                    {!selectedCertName && (
                                        <Badge ml={2} colorScheme="blue" fontSize="xs">
                                            Step 1
                                        </Badge>
                                    )}
                                </FormLabel>
                                <Select
                                    value={selectedCertName}
                                    placeholder='Select course from list...'
                                    bg={isReadOnly || isCourseAndIntakeLocked ? "gray.50" : "white"}
                                    onChange={(e) => {
                                        if (isCourseAndIntakeLocked) return;
                                        const certKey = e.target.value;
                                        setSelectedCertName(certKey);
                                        // Clear cert run selection when certification changes
                                        handleChange("draft_cert_run_code", '');
                                        
                                        // Check if new course is E-Learning and auto-select passport option
                                        setTimeout(() => {
                                            const certName = groupedCourseRuns[certKey]?.name || '';
                                            if (certName && certName.includes('(E-Learning)')) {
                                                const fieldConfig = basicFieldsOrder.find(f => f.field_key === 'nationality');
                                                const allOptions = (fieldConfig?.field_options && fieldConfig.field_options.length > 0) ? fieldConfig.field_options : DEFAULT_NATIONALITY_OPTION_VALUES;
                                                const passportOption = getFirstPassportOption(allOptions);
                                                if (passportOption && formData.nationality !== passportOption) {
                                                    handleChange("nationality", passportOption);
                                                    handleChange("nric", ''); // Clear NRIC since passport is selected
                                                }
                                            }
                                        }, 100);
                                    }}
                                    isDisabled={isReadOnly || isCourseAndIntakeLocked}
                                    size={{base: "md", md: "md"}}
                                    height={{base: "48px", md: "40px"}}
                                >
                                    {Object.keys(groupedCourseRuns).length > 0 ? (
                                        Object.keys(groupedCourseRuns)
                                            .sort((a, b) => {
                                                // Sort cert types alphabetically by name
                                                return groupedCourseRuns[a].name.localeCompare(groupedCourseRuns[b].name);
                                            })
                                            .map((certTypeKey) => {
                                                const certType = groupedCourseRuns[certTypeKey];
                                                return (
                                                    <option key={certTypeKey} value={certTypeKey}>
                                                        {certType.name}
                                                    </option>
                                                );
                                            })
                                    ) : (
                                        courseRuns.length > 0 && (
                                            <option value={courseRuns[0]?.full_certification?.code || courseRuns[0]?.full_cert_type_code || ''}>
                                                {courseRuns[0]?.full_certification?.name || 'Unknown Certification'}
                                            </option>
                                        )
                                    )}
                                </Select>
                                {Object.keys(groupedCourseRuns).length === 0 && courseRuns.length === 0 && (
                                    <Alert status="info" mt={2} borderRadius="md">
                                        <AlertIcon />
                                        <Text fontSize="sm">
                                            No certifications available at the moment. Please check back later or contact us for more information.
                                        </Text>
                                    </Alert>
                                )}
                            </FormControl>

                            {/* Step 2: Select Upcoming Batch (up to 6 runs) - Card Form */}
                            {selectedCertName && groupedCourseRuns[selectedCertName] && (
                                <FormControl>
                                    <FormLabel mb={4} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                                        Select Upcoming Batch
                                        <Badge ml={2} colorScheme="green" fontSize="xs">
                                            Step 2
                                        </Badge>
                                        {groupedCourseRuns[selectedCertName].runs.length > 6 && (
                                            <Badge ml={2} colorScheme="orange" fontSize="xs">
                                                Showing first 6 runs
                                            </Badge>
                                        )}
                                    </FormLabel>
                                    {!formData.draft_cert_run_code && (
                                        <Text mb={3} fontSize="sm" color="gray.600">
                                            Click a batch below to choose your schedule
                                        </Text>
                                    )}
                                    {groupedCourseRuns[selectedCertName].runs.length === 0 ? (
                                        <Alert status="warning" mt={2} borderRadius="md">
                                            <AlertIcon />
                                            <Text fontSize="sm">
                                                No course runs available for this course.
                                            </Text>
                                        </Alert>
                                    ) : (
                                        <SimpleGrid columns={{base: 1, md: 2, lg: 3}} spacing={{base: 3, md: 4}}>
                                            {(() => {
                                                const runs = groupedCourseRuns[selectedCertName].runs;
                                                // Detect runs that appear identical in the card display
                                                const signatureCounts = runs.reduce((acc, r) => {
                                                    const key = `${r.intake}|${r.start_date}|${r.end_date}`;
                                                    acc[key] = (acc[key] || 0) + 1;
                                                    return acc;
                                                }, {});
                                                const duplicateIds = new Set(
                                                    runs
                                                        .filter(r => signatureCounts[`${r.intake}|${r.start_date}|${r.end_date}`] > 1)
                                                        .map(r => r.id)
                                                );
                                                return runs.map((course) => {
                                                    const startDate = moment(course.start_date).format('D MMM, YYYY');
                                                    const endDate = moment(course.end_date).format('D MMM, YYYY');
                                                    const runName = course?.intake ? `Batch ${course.intake}` : 'Unnamed Run';
                                                    const isSelected = formData.draft_cert_run_code === course.id;
                                                    const isDisabled = isReadOnly || isCourseAndIntakeLocked;
                                                    const isDuplicate = duplicateIds.has(course.id);
                                                    
                                                    return (
                                                        <Box
                                                            key={course.id}
                                                            as="button"
                                                            type="button"
                                                            onClick={() => {
                                                                if (!isDisabled) {
                                                                    handleChange("draft_cert_run_code", course.id);
                                                                    
                                                                    // Check if selected course is E-Learning and auto-select passport option
                                                                    setTimeout(() => {
                                                                        // Use certification name from groupedCourseRuns (more reliable)
                                                                        const certName = groupedCourseRuns[selectedCertName]?.name || '';
                                                                        if (certName && certName.includes('(E-Learning)')) {
                                                                            const fieldConfig = basicFieldsOrder.find(f => f.field_key === 'nationality');
                                                                            const allOptions = (fieldConfig?.field_options && fieldConfig.field_options.length > 0) ? fieldConfig.field_options : DEFAULT_NATIONALITY_OPTION_VALUES;
                                                                            const passportOption = getFirstPassportOption(allOptions);
                                                                            if (passportOption && formData.nationality !== passportOption) {
                                                                                handleChange("nationality", passportOption);
                                                                                handleChange("nric", ''); // Clear NRIC since passport is selected
                                                                            }
                                                                        }
                                                                    }, 100);
                                                                }
                                                            }}
                                                            disabled={isDisabled}
                                                            borderWidth="2px"
                                                            borderRadius="xl"
                                                            p={{base: 3, md: 4}}
                                                            bg={isSelected ? "blue.50" : "white"}
                                                            borderColor={isSelected ? "blue.500" : "gray.300"}
                                                            boxShadow={isSelected ? "md" : "sm"}
                                                            transition="all 0.2s"
                                                            cursor={isDisabled ? "not-allowed" : "pointer"}
                                                            textAlign="left"
                                                            position="relative"
                                                            minH={{base: "120px", md: "auto"}}
                                                            _hover={!isDisabled ? {
                                                                borderColor: "blue.400",
                                                                boxShadow: "lg",
                                                                transform: "translateY(-2px)",
                                                                bg: isSelected ? "blue.50" : "gray.50"
                                                            } : {}}
                                                            _focus={{ outline: "none" }}
                                                            _focusVisible={{
                                                                outline: "2px solid",
                                                                outlineColor: "blue.400",
                                                                outlineOffset: "2px"
                                                            }}
                                                            _disabled={{
                                                                opacity: 0.5,
                                                                cursor: "not-allowed"
                                                            }}
                                                        >
                                                            {isSelected ? (
                                                                <Badge
                                                                    position="absolute"
                                                                    top={2}
                                                                    right={2}
                                                                    colorScheme="blue"
                                                                    fontSize="xs"
                                                                >
                                                                    Selected
                                                                </Badge>
                                                            ) : !isDisabled ? (
                                                                <Icon
                                                                    as={FaHandPointer}
                                                                    position="absolute"
                                                                    top={3}
                                                                    right={3}
                                                                    boxSize={5}
                                                                    color="gray.400"
                                                                    aria-hidden
                                                                />
                                                            ) : null}
                                                            <VStack align="stretch" spacing={2} pr={isSelected ? 0 : 6}>
                                                                <HStack spacing={2} align="center">
                                                                    <Text
                                                                        fontSize={{base: "sm", md: "md"}}
                                                                        fontWeight="700"
                                                                        color="gray.800"
                                                                        lineHeight="1.4"
                                                                    >
                                                                        {runName}
                                                                    </Text>
                                                                    {isDuplicate && (
                                                                        <Badge
                                                                            colorScheme="purple"
                                                                            fontSize="2xs"
                                                                            variant="subtle"
                                                                            flexShrink={0}
                                                                        >
                                                                            {course.id}
                                                                        </Badge>
                                                                    )}
                                                                </HStack>
                                                                <HStack spacing={2} fontSize={{base: "xs", md: "sm"}} color="gray.600">
                                                                    <Text fontWeight="600">Start:</Text>
                                                                    <Text>{startDate}</Text>
                                                                </HStack>
                                                                <HStack spacing={2} fontSize={{base: "xs", md: "sm"}} color="gray.600">
                                                                    <Text fontWeight="600">End:</Text>
                                                                    <Text>{endDate}</Text>
                                                                </HStack>
                                                            </VStack>
                                                        </Box>
                                                    );
                                                });
                                            })()}
                                        </SimpleGrid>
                                    )}
                                </FormControl>
                            )}
                        </VStack>

                        {/* Display Selected Modules - hidden per client request
                        {selectedCourseRun && (
                            <Box mb={6}>
                                <Flex justify="space-between" align="center" mb={2}>
                                    <FormLabel m={0} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                                        Course Modules
                                        <Badge ml={2} colorScheme="green" fontSize="xs">
                                            {selectedCourseRun.length} {selectedCourseRun.length === 1 ? 'Module' : 'Modules'}
                                        </Badge>
                                    </FormLabel>
                                    <Button 
                                        size="xs" 
                                        variant="ghost" 
                                        colorScheme="brand" 
                                        onClick={() => setShowModules(!showModules)}
                                        rightIcon={showModules ? <HiChevronUp /> : <HiChevronDown />}
                                    >
                                        {showModules ? "Hide" : "View Modules"}
                                    </Button>
                                </Flex>
                                
                                {showModules && (
                                    <SimpleGrid columns={{base: 1, md: 2, lg: 3}} spacing={{base: 4, md: 6}} mt={4}>
                                        {selectedCourseRun
                                            ?.slice()
                                            .sort((a, b) => {
                                                const getSortDate = (course) => {
                                                    const courseRun = course?.course_run;
                                                    if (courseRun?.course_dates && courseRun.course_dates.length > 0) {
                                                        return new Date(courseRun.course_dates[0].date);
                                                    }
                                                    return courseRun?.start_date ? new Date(courseRun.start_date) : new Date('9999-12-31');
                                                };
                                                const dateA = getSortDate(a);
                                                const dateB = getSortDate(b);
                                                return dateA - dateB;
                                            })
                                            .map((course) => (
                                                <ModuleCard
                                                    key={course.id}
                                                    course_name={course?.course_run?.course_type.name}
                                                    start_date={course?.course_run?.start_date}
                                                    end_date={course?.course_run?.end_date}
                                                    course_dates={course?.course_run?.course_dates}
                                                    course_type_code={course?.course_run?.course_type_code}
                                                    module_tag={course?.course_run?.module_tag}
                                                />
                                            ))}
                                    </SimpleGrid>
                                )}
                            </Box>
                        )}
                        */}

                                {/* Only show form if certification run is selected */}
                        {formData.draft_cert_run_code && (
                            <>
                                {/* Information banner - removed two-step process */}

                                {/* Basic required fields */}
                                {(basicFieldsOrder.length > 0 || compulsoryCustomFields.length > 0) && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: 0.1 }}
                                    >
                                    <Box mt={{base: 4, md: 6}} mb={4}>
                                        <VStack spacing={{base: 3, md: 4}} align="stretch">
                                            {basicFieldsOrder.map((field) => renderBasicField(field.field_key))}
                                            {compulsoryCustomFields
                                                .filter((field) => templateFieldIds === null || !field.id || templateFieldIds.includes(field.id))
                                                .map((field) => renderCompulsoryCustomField(field))}
                                        </VStack>
                                    </Box>
                                    </motion.div>
                                )}

                                {/* Additional Fields Section */}
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: 0.2 }}
                                >
                                <Box ref={additionalFieldsRef} mt={8} p={6} borderWidth="1px" borderRadius="lg" borderColor="brand.200" bg="brand.50">
                                        <AdditionalFields
                                            formData={formData}
                                            setFormData={setFormData}
                                            validationErrors={validationErrors}
                                            setValidationErrors={setValidationErrors}
                                            registrationId={registrationId}
                                            setRegistrationId={setRegistrationId}
                                            isReadOnly={isReadOnly}
                                            templateFieldIds={templateFieldIds}
                                            selectedFiles={selectedFiles}
                                            setSelectedFiles={setSelectedFiles}
                                            uploadingFiles={uploadingFiles}
                                            setUploadingFiles={setUploadingFiles}
                                            onAutoSave={async () => {
                                                // Auto-save a draft registration if needed for file uploads
                                                if (!registrationId && formData.participant_name && formData.participant_email && formData.draft_cert_run_code) {
                                                    try {
                                                        const registrationData = {
                                                            participant_name: formData.participant_name,
                                                            participant_email: formData.participant_email,
                                                            nationality: formData.nationality || '',
                                                            nric: formData.nric || '',
                                                            dob: formData.dob || '',
                                                            birth_age: formData.birth_age || '',
                                                            mobile_no: formData.mobile_no || '',
                                                            registration_stage: "draft",
                                                            draft_cert_run_code: formData.draft_cert_run_code,
                                                        };
                                                        
                                                        const { data, error } = await supabase
                                                            .from('registration')
                                                            .insert([registrationData])
                                                            .select("id")
                                                            .single();
                                                        
                                                        if (error) throw error;
                                                        
                                                        const savedId = data.id;
                                                        setRegistrationId(savedId);
                                                        
                                                        // Send verification email only if required by policy
                                                        if (formData.participant_email && needsEmailVerification) {
                                                            await sendVerificationEmail(savedId, formData.participant_email, formData.participant_name);
                                                        }
                                                        
                                                        return savedId;
                                                    } catch (error) {
                                                        console.error('Auto-save error:', error);
                                                        toast({
                                                            title: "Auto-save Failed",
                                                            description: "Could not save registration. Please fill in all required fields.",
                                                            status: "error",
                                                            duration: 5000,
                                                            isClosable: true,
                                                        });
                                                        return null;
                                                    }
                                                }
                                                return null;
                                            }}
                                        />
                                </Box>
                                </motion.div>


                                {/* Submit Registration Button */}
                                {!isReadOnly && progressData.canSubmit && (
                                <Box
                                    position="fixed"
                                    bottom={0}
                                    left={0}
                                    right={0}
                                    bg="white"
                                    borderTop="1px solid"
                                    borderColor="gray.200"
                                    py={{base: 3, md: 4}}
                                    px={{base: 4, md: 6}}
                                    zIndex={50}
                                    boxShadow="0 -4px 20px rgba(0,0,0,0.1)"
                                >
                                <Flex 
                                    justifyContent="center"
                                    align="center"
                                    gap={3} 
                                    direction="column"
                                    maxW="600px"
                                    mx="auto"
                                >
                                    <Button
                                        isLoading={isSubmitting}
                                        size="lg"
                                        width="100%"
                                        colorScheme="brand"
                                        height={{base: "54px", md: "50px"}}
                                        fontSize={{base: "md", md: "md"}}
                                        boxShadow="lg"
                                        borderRadius="xl"
                                        onClick={async () => {
                                            // 1. Validation for Required Fields (respect active registration template)
                                            const missingFields = [];
                                            const labelFor = (key, fallback) =>
                                                getOriginalFieldConfig(key)?.field_label || fallback;

                                            if (fieldInTemplate('nationality') && !formData.nationality) {
                                                missingFields.push(labelFor('nationality', 'Nationality'));
                                            }
                                            if (fieldInTemplate('nric')) {
                                                const nricApplies = isSingaporeCitizenOrPR(formData.nationality) ||
                                                    requiresPassport(formData.nationality) ||
                                                    requiresFIN(formData.nationality);
                                                if (nricApplies && !formData.nric) {
                                                    missingFields.push(
                                                        isSingaporeCitizenOrPR(formData.nationality)
                                                            ? labelFor('nric', 'NRIC')
                                                            : 'Passport/FIN Number'
                                                    );
                                                }
                                            }
                                            if (fieldInTemplate('dob') && !formData.dob) {
                                                missingFields.push(labelFor('dob', 'Date of Birth'));
                                            }
                                            if (fieldInTemplate('participant_name') && !formData.participant_name) {
                                                missingFields.push(labelFor('participant_name', 'Participant Name'));
                                            }
                                            if (fieldInTemplate('participant_email') && !formData.participant_email) {
                                                missingFields.push(labelFor('participant_email', 'Email Address'));
                                            }
                                            if (fieldInTemplate('mobile_no') && !formData.mobile_no) {
                                                missingFields.push(labelFor('mobile_no', 'Mobile Number'));
                                            }

                                            // Check validation errors (only for fields in template)
                                            if (fieldInTemplate('participant_email') && validationErrors.email) {
                                                missingFields.push('Valid Email Address');
                                            }
                                            if (fieldInTemplate('mobile_no') && validationErrors.mobile) {
                                                missingFields.push('Valid Mobile Number');
                                            }
                                            if (fieldInTemplate('dob') && validationErrors.dob) {
                                                missingFields.push('Valid Date of Birth');
                                            }
                                            if (fieldInTemplate('nric') && validationErrors.nric) {
                                                missingFields.push('Valid Identification Number');
                                            }

                                            if (missingFields.length > 0) {
                                                toast({
                                                    title: "Missing Required Fields",
                                                    description: `Please fill in: ${missingFields.join(', ')}`,
                                                    status: "error",
                                                    duration: 5000,
                                                    isClosable: true,
                                                });
                                                return;
                                            }

                                            // 2. Validation for Additional Required Fields (including staged files)
                                            if (!checkRequiredAdditionalFields()) {
                                                const missingAdditional = additionalFields.filter(field => {
                                                    const isRequired = getEffectiveRequiredStatus(field);
                                                    const fieldValue = getFieldValue(formData, field.field_key);
                                                    const stagedFiles = selectedFiles[field.field_key];
                                                    const hasValue = (fieldValue && fieldValue !== '' && (!Array.isArray(fieldValue) || fieldValue.length > 0));
                                                    const hasStagedFiles = (stagedFiles && stagedFiles.length > 0);
                                                    return isRequired && !hasValue && !hasStagedFiles;
                                                }).map(f => f.field_label);

                                                toast({
                                                    title: "Missing Required Fields",
                                                    description: `Please fill in: ${missingAdditional.join(', ')}`,
                                                    status: "error",
                                                    duration: 5000,
                                                    isClosable: true,
                                                });
                                                return;
                                            }

                                            // All checks passed, submit
                                            handleSubmit();
                                        }}
                                    >
                                        {formData.registration_stage === 'rejected'
                                            ? 'Submit Amendment'
                                            : 'Submit Registration'}
                                    </Button>
                                </Flex>
                                </Box>
                                )}
                            </>
                        )}
                    </Box>
                </Stack>
            )}

        </div>
    );
};

export default FullCertRegistration;
